export interface ChangelogEntry {
  id: string;
  date: string;
  title: string;
  items: { type: "feature" | "improvement" | "fix"; text: string }[];
  /**
   * Flag-gated entry (docs/agents/CHANGELOG.md): hidden from What's
   * New until this `feature_flags` key is on for the viewer's organization, then announced
   * once, whenever that happens (see ChangelogModal's per-browser gated-seen list). Pair with
   * `requiresValue` for an enum flag, e.g. `requiresFlag: "ui_uplift", requiresValue: "enabled"`.
   * Reads `feature_flags` rows only (FeatureFlagProvider), not code defaults: a flag switched on
   * purely in code never reveals the entry, so promotion assembly for that flip replaces `requiresFlag`
   * (and `requiresValue`) with `announceOnce: true`.
   */
  requiresFlag?: string;
  requiresValue?: string;
  /** Announce this entry once per browser even if newer entries were already seen: what a
   * formerly gated entry keeps once its gate is dropped, so it is still announced. */
  announceOnce?: boolean;
}

/** Runtime flag state as FeatureFlagProvider resolves it for the current organization. */
export type ChangelogFlagState = Record<string, { enabled: boolean; value: string | null } | undefined>;

/** Whether `entry` is visible to someone whose organization has these flags. Ungated entries
 * always are; a gated one only once its flag is enabled (and matches `requiresValue`). */
export function isChangelogEntryVisible(entry: ChangelogEntry, flags: ChangelogFlagState): boolean {
  if (!entry.requiresFlag) return true;
  const flag = flags[entry.requiresFlag];
  if (!flag?.enabled) return false;
  return entry.requiresValue === undefined || flag.value === entry.requiresValue;
}

/** Parses a changelog `id` into its date and same-day sequence number, for
 * correct ordering. IDs are `YYYY-MM-DD` or `YYYY-MM-DD-N` for the Nth entry
 * shipped the same day (see the comment below CHANGELOG_ENTRIES) — comparing
 * the whole string lexicographically breaks once N reaches double digits
 * (`"2026-09-13-10" < "2026-09-13-9"` as strings, even though -10 shipped
 * later), so the date and the numeric suffix are compared separately
 * instead. A bare `YYYY-MM-DD` (no suffix) is the first entry of that day,
 * so it sorts before any suffixed variant of the same day — suffix 0. An id
 * that doesn't match the documented format falls back to comparing as a
 * plain string with no suffix, so it degrades gracefully instead of
 * throwing. */
function parseChangelogId(id: string): { datePart: string; suffix: number } {
  const match = /^(\d{4}-\d{2}-\d{2})(?:-(\d+))?$/.exec(id);
  if (!match) {
    return { datePart: id, suffix: 0 };
  }
  return { datePart: match[1], suffix: match[2] ? Number(match[2]) : 0 };
}

/** Whether changelog id `a` shipped strictly after id `b`. Used everywhere
 * two changelog ids need to be ordered — ChangelogModal's unseen check and
 * mark-seen write guard, and usePersonalizationPreferences' cross-device
 * hydration reconciliation — so there is exactly one comparison rule to get
 * right, not several copies that can silently disagree. */
export function isChangelogIdNewer(a: string, b: string): boolean {
  const parsedA = parseChangelogId(a);
  const parsedB = parseChangelogId(b);
  if (parsedA.datePart !== parsedB.datePart) {
    return parsedA.datePart > parsedB.datePart;
  }
  return parsedA.suffix > parsedB.suffix;
}

// This general ledger describes product behavior for its stated flag audience.
// Keep organization names, customer records, one-off cleanup/backfill work and
// support-incident details in internal operational records, not these entries.
// Never turn an organization-only operation into a broadly advertised feature.
// Newest first. Ordinary feature/fix PRs supply release-note text in their
// body/comments; only the promotion owner assembles entries in this ledger.
// See docs/agents/CHANGELOG.md. Do not add per-PR entries or fragment files.
//
// Once an entry has SHIPPED (its PR merged/deployed), never append items to
// it in place. "Unseen" is determined purely by comparing the newest entry's
// `id` against a user's stored lastSeenChangelogId — NOT by diffing content —
// so a user who already saw entry "2026-09-13" and then gets a silently
// mutated "2026-09-13" with a new item appended will never be notified: their
// stored id still matches and ChangelogModal treats the entry as seen. Any
// post-ship addition — even a single follow-up item — MUST get its own new,
// unique `id` (e.g. a later date, or a suffixed variant) so it registers as
// unseen for everyone who already saw the prior entry.
export const CHANGELOG_ENTRIES: ChangelogEntry[] = [
  {
    id: "2026-11-03-4",
    date: "2026-11-03",
    title: "Clearer account management for platform administrators",
    items: [
      { type: "improvement", text: "For platform administrators, bulk-disabling users also ends their active sign-ins and reports users whose disable failed as skipped. Deleting a user who still owns data that prevents deletion now explains why and leaves the user unchanged." },
      { type: "improvement", text: "Disabled or banned administrators can no longer use organization management, MFA reset, the job dispatcher, Zoho setup and CRM tools, the demo-data seeder, dead-letter resolution or Stripe event replay with a sign-in issued before their account was disabled." },
    ],
  },
  {
    id: "2026-11-03-3",
    date: "2026-11-03",
    title: "More flexible repeating events",
    items: [
      { type: "feature", text: "Schedule monthly events on the first, second, third, fourth or last weekday, such as the first Wednesday of each month." },
      { type: "feature", text: "Public event listings and church websites can show each upcoming occurrence of a repeating event, leaving out dates you have cancelled." },
    ],
  },
  {
    id: "2026-11-03-2",
    date: "2026-11-03",
    title: "More reliable finance tools and everyday controls",
    items: [
      { type: "fix", text: "Sending an email or message no longer silently repeats a request after a partial delivery failure. Forge reports the actual error instead of risking a duplicate send." },
      { type: "improvement", text: "Contribution entry asks before discarding unsaved gifts and keeps an unsaved batch when the page reloads or a tab is restored." },
      { type: "improvement", text: "Bank statement imports can read OFX, QFX and QBO files. CSV imports preview the chosen date, description and amount columns, let you correct the match, and remember it for that bank account." },
      { type: "improvement", text: "Importing pledges saves each pledge, its fund and its history together. A row that cannot be saved explains why and leaves nothing partially saved." },
      { type: "improvement", text: "Organization admins can choose which supported roles may cancel pledges in Pledge settings. Until an organization chooses, cancellation is limited to Admin, Finance and Finance admin." },
      { type: "fix", text: "Bookkeepers, finance administrators and treasurers can read their organization's gifts again. Treasurers can also see contribution entries awaiting posting; posting remains with bookkeepers and finance administrators." },
      { type: "fix", text: "Recurring giving totals include every schedule, and 1099 preparation includes every vendor payment in the selected tax year instead of only the 100 most recent payments. Check prior-year vendor totals before filing. Large finance and volunteer lists no longer count a row twice if one is added while they load." },
      { type: "fix", text: "Accounts Receivable uses your organization's currency for fee schedules, discounts, financial aid and late-fee policies, and the invoice's currency for payment plans and assessed fees. Statement balances without a single known currency no longer show an assumed symbol." },
      { type: "fix", text: "The finance compliance queue labels board-designated funds correctly. The dashboard hides a reconciliation card when your role cannot read it, and Accounts Payable settings explain a refused read instead of showing an assumed attachment setting." },
      { type: "fix", text: "Finance settings open a page your organization can use. A bank-account rename that is refused reports the failure, QuickBooks Disconnect asks for confirmation, and approval policies explain why a policy already in use cannot be deleted. Unsaved approval switches clear when another administrator saves the same value." },
      { type: "improvement", text: "Journal history shows who made each change. Role changes, waitlist promotions, duplicate merges, offline sync, registrations, requests and household changes record their audit history more reliably." },
      { type: "fix", text: "Organization admins can change member roles from Team settings again, while ownership changes remain owner-only." },
      { type: "fix", text: "Printed reports again include their masthead. Report loading failures explain the problem and offer Try again. The Home giving-and-expenses widget compares monthly bars with amount tooltips and a monthly table." },
      { type: "fix", text: "Volunteer-hours exports keep names containing commas in one column and prevent names from being interpreted as spreadsheet formulas. Renewing an old certification gives it a current expiry date, and editing a certification no longer offers a volunteer change it cannot save." },
      { type: "fix", text: "New recurring expenses start with today's date even in a tab left open overnight. The classic conditional-contribution page shows Record Contribution again." },
      { type: "improvement", text: "Keyboard focus returns to the page title after saving settings and to the originating menu after closing a record confirmation. Organizations and sermons can be opened from the keyboard, and sermon details close back to the item you opened." },
      { type: "fix", text: "Account and vendor pickers, the QuickBooks connection link and the event Description field have clearer accessible names. Dialog footers stay visible without covering focus rings, older phone browsers keep drawers within the screen, and landscape check-in stations retain large touch targets." },
      { type: "improvement", text: "Status colors in fund templates, contribution batches, security settings, manual journals, vendor 1099s and Accounts Payable are easier to read in dark mode. Team confirmation buttons keep readable text in both themes." },
      { type: "fix", text: "HR notes now save correctly, assigned reviewers can see their reviews without HR administrator access, and compensation loading failures explain the problem and offer Try again." },
      { type: "improvement", text: "Creating curriculum, creating or editing a fund, and recording a conditional contribution use the shared side drawer instead of a centered dialog." },
    ],
  },
  {
    id: "2026-11-03-1",
    date: "2026-11-03",
    title: "Clearer workspaces in the refreshed Forge layout",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Sending-domain setup is a guided task with progress, DNS status and test-email details beside each step. You can leave and resume from Email integrations." },
      { type: "improvement", text: "The bulletin editor uses a full workspace with sections, a page canvas and settings alongside. Click a block to edit it, or click the empty page or press Escape to return to bulletin settings." },
      { type: "improvement", text: "HR staff, requests, reviews, compensation, benefits and documents have clearer lists with relevant views, search and filters. Staff profiles bring employment details, notes, reviews, pay, benefits, documents and time off together." },
      { type: "improvement", text: "The HR overview links its summary figures to the matching lists and highlights starts, departures, requests and documents awaiting action. HR settings list unsaved changes in a save bar." },
      { type: "improvement", text: "Sermons, certifications and service hours use searchable, sortable lists with relevant views. Volunteer histories open alongside the list, and destructive actions ask for confirmation." },
      { type: "improvement", text: "Ministry records have linked Roster, Serving teams, Groups and Details tabs. Roster filters survive going back, and removing a member asks first." },
      { type: "improvement", text: "Board packs, monthly summaries and financial statements have clearer record layouts, direct links to sections or statements, and prominent print, download, export or share actions. Screen readers hear whether summary figures trend up, down or stay steady." },
      { type: "improvement", text: "Recurring giving, in-kind gifts and conditional contributions have focused views and clearer next actions. In-kind voids ask for a reason, while conditional gifts show their conditions, notes and next recognition or return step." },
      { type: "improvement", text: "Purchase orders and recurring expenses have stage-based views and bulk actions that explain how many selected records are eligible. A lack of access is distinguished from an empty list." },
      { type: "improvement", text: "Chart of Accounts shows an expandable account tree with Active, All and Inactive views, search, type filters, account drawers and Export and Print. 1099 preparation adds a year picker and views around the reporting threshold." },
    ],
  },
  {
    id: "2026-10-28-3",
    date: "2026-10-28",
    title: "More readable status colors in dark mode",
    items: [
      { type: "improvement", text: "In dark mode, the \"Expiring soon\" badges on a staff member's or volunteer's training records and background checks (both under Advanced details on their profile) are now easier to read, and the warning that owners and admins see on Kids Check-In when it couldn't check whether today's children have a parent or guardian on record now uses a softer tint instead of a bright box. Light mode is unchanged." },
      { type: "improvement", text: "In dark mode, the Allocated total when you split a bank transaction in Banking, the Disable Schedule button on a report or board pack that already has an active schedule, and the \"Breached by\" time in the Breached items list under Settings → Today queue are now easier to read. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-28-2",
    date: "2026-10-28",
    title: "Softer dark mode for plan badges and tags",
    items: [
      { type: "improvement", text: "In dark mode, the Plant, Harvest and Flourish plan badges on Settings → Subscription and in the upgrade prompts for features that need a higher plan now use softer, more muted tints. In light mode, only the Flourish badge changes, to a slightly deeper purple." },
      { type: "improvement", text: "In dark mode, tags now use soft tinted colors instead of pale pastel pills. This applies to the color choices and tag list in Settings → Tags, the tags under Advanced details on a person's profile, and the tag filter chips on the People page. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-28-1",
    date: "2026-10-28",
    title: "A cleaner look for buttons, fields and pop-ups",
    items: [
      { type: "improvement", text: "On larger screens, buttons, text boxes and dropdowns are now slimmer and have gently rounded corners instead of fully rounded ends. On phones and smaller screens they stay a larger, easy-to-tap size." },
      { type: "improvement", text: "The main gold button is now a single solid gold instead of a gradient." },
      { type: "improvement", text: "Pop-ups and side panels now use a solid panel over a plain dimmed backdrop instead of a blurred, see-through one." },
      { type: "improvement", text: "The tabs on record pages, such as a person, event, pledge or request, are now underlined instead of shown as a tinted pill." },
      { type: "improvement", text: "On larger screens, the top bar is slightly shorter." },
      { type: "fix", text: "Text boxes, dropdowns, text areas and outline buttons now have a clearer, higher-contrast edge in both light and dark themes, so it is easier to see where to click or type." },
      { type: "fix", text: "In light mode, the selected tab on tab bars and the selected filter in Search now use darker text on their pale gold highlight, so the label is easier to read." },
    ],
  },
  {
    id: "2026-10-28",
    date: "2026-10-28",
    title: "Where to cancel a pledge",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "fix", text: "Correction to our earlier update on the pledge page: More actions now holds only Edit pledge, and Cancel pledge is no longer there. You can still cancel a pledge from the Pledges list." },
    ],
  },
  {
    id: "2026-10-27-2",
    date: "2026-10-27",
    title: "Softer dark mode for insights, org chart and a few badges",
    items: [
      { type: "improvement", text: "In dark mode, the report insight cards, the HR org chart cards, the Expired badge on Background Checks and the Stable badge on Engagement Scoring now use the same readable colors as the rest of Forge. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-27-1",
    date: "2026-10-27",
    title: "Schedule preview opens in a side panel",
    items: [
      { type: "improvement", text: "Reviewing a proposed volunteer schedule now opens in a side panel (a bottom sheet on a phone) instead of a pop-up, and its title, description and Close button stay in view while you scroll through the assignments." },
    ],
    requiresFlag: "auto_scheduler",
  },
  {
    id: "2026-10-26-7",
    date: "2026-10-26",
    title: "Adding team members opens in a side panel",
    items: [
      { type: "improvement", text: "Adding people to a serving team now opens in a side panel (a bottom sheet on a phone) instead of a pop-up, so the whole list of people scrolls with the panel and the Add button stays in view." },
    ],
  },
  {
    id: "2026-10-26-6",
    date: "2026-10-26",
    title: "Volunteer qualifications open in a side panel",
    items: [
      { type: "improvement", text: "Setting which volunteers are qualified for a position, from the Volunteers tab of a service, now opens in a side panel (a bottom sheet on a phone) instead of a pop-up, and the volunteer list now scrolls with the panel instead of inside its own box." },
    ],
  },
  {
    id: "2026-10-26-5",
    date: "2026-10-26",
    title: "Softer gold highlights for the theme picker in dark mode",
    items: [
      { type: "improvement", text: "In dark mode, the selected choice in the Appearance card on My preferences and in the Theme switch in your profile menu now shows in a softer gold. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-26-4",
    date: "2026-10-26",
    title: "Starting a new message opens in a side panel",
    items: [
      { type: "improvement", text: "Starting a new direct message now opens in a side panel (a bottom sheet on a phone) instead of a pop-up, so the list of people uses the full height of the panel and scrolls inside it, and Cancel stays in view." },
    ],
  },
  {
    id: "2026-10-26-3",
    date: "2026-10-26",
    title: "Choose how far back the Finance dashboard looks at giving",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "The Finance dashboard now has 3, 6 and 12 month buttons at the top. They change how many months the Average Monthly Giving card covers. Cash, expenses and the numbers in the four cards above are not affected." },
    ],
  },
  {
    id: "2026-10-26-2",
    date: "2026-10-26",
    title: "Softer dark mode for member and staff status messages",
    items: [
      { type: "improvement", text: "In dark mode, the success and warning messages on member group and notification pages, the staff dashboard, Directory settings and the Today SLA health panel now use the same softer status tints as the rest of Forge, so they are easier to read. Light mode is unchanged." },
      { type: "improvement", text: "In dark mode, the connected-Stripe status and the Kids Check-In confirmation panel now use the same softer success tint. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-26-1",
    date: "2026-10-26",
    title: "Creating and deleting funds stay with the right church",
    items: [
      { type: "fix", text: "Creating a fund, or deleting one, is saved for the church you made the change in, even if you switch churches before your connection returns." },
    ],
  },
  {
    id: "2026-10-26",
    date: "2026-10-26",
    title: "Creating a calendar event",
    items: [
      { type: "improvement", text: "Creating an event from the calendar now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Cancel and Create Event kept in view as you fill in the details." },
    ],
  },
  {
    id: "2026-10-25-4",
    date: "2026-10-25",
    title: "A Finance dashboard side panel that only shows what needs you",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "The \"What needs you\" panel on the Finance dashboard now lists only things you can act on. Each fund that needs attention opens the compliance queue, and the month-end close card opens Month-End Close. Status-only lines, like \"All funds compliant\" and the next restriction expiring, no longer take up room there; upcoming expirations are still listed further down the page." },
    ],
  },
  {
    id: "2026-10-25-3",
    date: "2026-10-25",
    title: "Creating an event opens in a side panel",
    items: [
      { type: "improvement", text: "Creating an event now opens in a side panel (a bottom sheet on a phone) instead of a pop-up, so the long form scrolls as one page and Create Event stays in view." },
    ],
  },
  {
    id: "2026-10-25-2",
    date: "2026-10-25",
    title: "Logging volunteer hours opens in a side panel",
    items: [
      { type: "improvement", text: "Logging service hours for a volunteer now opens in a side panel (a bottom sheet on a phone) instead of a pop-up, so the whole form fits without scrolling inside a small box and Log Hours stays in view." },
    ],
  },
  {
    id: "2026-10-25-1",
    date: "2026-10-25",
    title: "Draft invoices stay visible",
    items: [{ type: "fix", text: "Accounts Receivable now lists draft invoices separately, so newly created invoices remain visible without increasing collectible balances before they are posted." }],
  },
  {
    id: "2026-10-25",
    date: "2026-10-25",
    title: "Academics asks for a school year first",
    items: [{ type: "improvement", text: "On the Schools Academics page, Levels, Sections and Rosters now stay locked until you create an academic year, with a note explaining why, so you no longer fill out a form that can't be saved." }],
  },
  {
    id: "2026-10-23-2",
    date: "2026-10-23",
    title: "Budget comparisons state what can be verified",
    items: [{ type: "fix", text: "Choose an approved budget and dates for Budget vs Actual. Comparisons stay unavailable until the budget's calendar, amounts and currency are confirmed, so missing information is never shown as zero. Scheduled budget delivery is unavailable while it cannot verify that selection." }],
  },
  {
    id: "2026-10-23",
    date: "2026-10-23",
    title: "Giving statement downloads",
    announceOnce: true,
    items: [
      { type: "fix", text: "Giving statement downloads check access each time and open a fresh private link." },
      { type: "fix", text: "For older statements that show a restoration message, ask your church administrator to arrange restoration of the private download before retrying." },
    ],
  },
  {
    id: "2026-10-22-1",
    date: "2026-10-22",
    title: "More reliable access and Finance details",
    announceOnce: true,
    items: [
      { type: "improvement", text: "In dark mode, the In-Kind Contributions page now uses the same readable text, borders and info tints as the rest of Forge, so labels, totals and the category picker are easier to see. Light mode is unchanged." },
      { type: "fix", text: "Bank deposit account choices refresh before you can use them, so archived accounts are not carried over from an earlier visit." },
      { type: "fix", text: "Pages and features finish checking your access without waiting for an unrelated subscription-status request." },
      { type: "fix", text: "Payables and receivables refresh their complete lists before showing current totals or enabling actions. Invoice counts and open balances use the same complete list, individual amounts retain their recorded currencies, and pending or failed reads show loading or unavailable states." },
    ],
  },
  {
    id: "2026-10-22",
    date: "2026-10-22",
    title: "Report dates reflect the results",
    items: [{ type: "fix", text: "Reports now show the dates their results actually cover, including printed and PDF copies. Date controls appear only when they affect the report." }],
  },
  {
    id: "2026-10-21",
    date: "2026-10-21",
    title: "More reliable Finance overview details",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "fix", text: "Finance overview links keep your preview layout, dashboard panels use the available space, and New deposit waits for a refreshed active bank account. Dashboard amounts use their known currencies; summaries that do not establish a currency show it as unavailable." },
    ],
  },
  {
    id: "2026-10-20-5",
    date: "2026-10-20",
    title: "A clearer Finance dashboard",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "The Finance dashboard now opens with four numbers you can click: cash on hand, bills to pay, invoices owed to you, and entries waiting to post. Each one opens the list behind it, and the count under Waiting to post always matches that list. New deposit is the one action at the top right." },
    ],
  },
  {
    id: "2026-10-20-4",
    date: "2026-10-20",
    title: "Adding a chart of accounts entry",
    items: [
      { type: "improvement", text: "Adding or editing an account in the chart of accounts now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Delete, Cancel and Save kept in view as you fill in the details." },
    ],
  },
  {
    id: "2026-10-20-3",
    date: "2026-10-20",
    title: "Fund edits stay with the right church",
    items: [
      { type: "fix", text: "Editing a fund, or pausing and reactivating it, is saved for the church you made the change in, even if you switch churches before your connection returns." },
    ],
  },
  {
    id: "2026-10-20-2",
    date: "2026-10-20",
    title: "Adding a group member opens in a side panel",
    items: [
      { type: "improvement", text: "Adding someone to a group now opens in a side panel (a bottom sheet on a phone) instead of a pop-up, with the search box and the people list together and Cancel always in view. The group roster steps aside while it is open and comes back when you finish." },
    ],
  },
  {
    id: "2026-10-20-1",
    date: "2026-10-20",
    title: "Settings confirmations name the church they were saved in",
    items: [
      { type: "improvement", text: "If you switch churches while a settings change is still saving, the change finishes in the church you made it in, and for general settings the confirmation now names that church so it is clear where it went." },
    ],
  },
  {
    id: "2026-10-20",
    date: "2026-10-20",
    title: "Settings saves work together",
    items: [
      { type: "fix", text: "Saving two settings at the same time preserves both changes. A background refresh no longer hides a change while it is saving, and a stalled save no longer blocks later saves indefinitely." },
    ],
  },
  {
    id: "2026-10-19-7",
    date: "2026-10-19",
    title: "Settings stay with the right church",
    items: [
      { type: "fix", text: "Module and workflow changes use the settings of the church being supported, without copying another church's choices." },
      { type: "fix", text: "Changes to Today response windows, warning thresholds and role-specific targets stay with the church where you made them, even if you switch churches while offline." },
    ],
  },
  {
    id: "2026-10-19-6",
    date: "2026-10-19",
    title: "Team changes stay with the right church",
    items: [
      { type: "fix", text: "If you change someone's roles, change their role, or remove them on Settings > Team and then switch to another church (or your connection returns after being offline), the change is saved for the church you made it in, not the one you switched to." },
      { type: "fix", text: "The same holds on Settings > Permissions: role changes, access reviews, and the scopes you add to a role are saved for the church you made them in, even if you switch churches before they go through." },
      { type: "fix", text: "Creating, renaming, or deleting a tag, and adding tags to people, are likewise saved for the church you made the change in, even if you switch churches before your connection returns." },
    ],
  },
  {
    id: "2026-10-19-5",
    date: "2026-10-19",
    title: "Adding an expense allocation rule",
    items: [
      { type: "improvement", text: "Adding or editing an expense allocation rule now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Cancel and Save kept in view as you fill in the details." },
    ],
  },
  {
    id: "2026-10-19-4",
    date: "2026-10-19",
    title: "Module and workflow settings stay with the right church",
    items: [
      { type: "fix", text: "Turning a module or an automatic workflow on or off is saved for the church you made the change in, even if you switch churches before your connection returns." },
    ],
  },
  {
    id: "2026-10-19-3",
    date: "2026-10-19",
    title: "Softer dark mode for fund, automation and team badges",
    items: [
      { type: "improvement", text: "In dark mode, the Online badge on Funds, the status chips on Automations and the pending-invite badge in Team settings now use the same softer status tints as the rest of Forge, so they are easier to read. Light mode is unchanged." },
      { type: "improvement", text: "In dark mode, the \"are you sure?\" prompt on protected settings, the error stat cards, the balanced notice on opening balances, the group roster warning and the welcome tour tip now use the same softer status tints, so they are easier to read. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-19-2",
    date: "2026-10-19",
    title: "Softer dark mode for attention alerts",
    requiresFlag: "today_attention_queue_v1",
    items: [
      { type: "improvement", text: "In dark mode, the red and amber alert badges in the Today attention list now use the same softer status tints as the rest of Forge, so they are easier to read. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-19-1",
    date: "2026-10-19",
    title: "Softer dark mode for kids check-in alerts",
    items: [
      { type: "improvement", text: "In dark mode, the allergy and pickup-alert banners in Kids Check-In now use the same softer status tints as the rest of Forge, so they are easier to read. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-19",
    date: "2026-10-19",
    title: "Ministry area changes stay with the right church",
    items: [
      { type: "fix", text: "Adding, editing, or deleting a ministry area is saved for the church you made the change in, even if you switch churches before your connection returns." },
    ],
  },
  {
    id: "2026-10-18-9",
    date: "2026-10-18",
    title: "Booking a room",
    items: [
      { type: "improvement", text: "Booking a room now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Cancel and Book Room kept in view as you fill in the details." },
    ],
  },
  {
    id: "2026-10-18-8",
    date: "2026-10-18",
    title: "Softer lines and module colours on the new pages",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "fix", text: "The lines around cards, filter bars and saved views, and between settings rows, draw as the light lines they were designed to be instead of dark outlines." },
      { type: "fix", text: "The magnifying glass shows again at the start of the search box above lists." },
      { type: "improvement", text: "The selected tab, the selected saved view, filter chips and timeline dots use the colour of the part of Forge you are in (green in Finance, blue in People), in dark mode too. The selected tab and a side panel's Open full record link are now easy to read." },
      { type: "fix", text: "Pages such as Groups, Care, Documents and Requests now use the colour of the part of Forge they belong to (blue in People, orange in Operations) instead of gold, and so do the filter chips inside the Filters panel on a phone." },
      { type: "fix", text: "In dark mode, a section with nothing in it yet no longer shows a pale panel with a title you couldn't read." },
    ],
  },
  {
    id: "2026-10-18-7",
    date: "2026-10-18",
    title: "Lighter lines and clearer red buttons",
    items: [
      { type: "fix", text: "The edge of outline buttons is now the same soft grey as the edge of text boxes, and the lines between table rows are light, instead of dark outlines, on every page." },
      { type: "fix", text: "Delete confirmations show a red button instead of a gold one on many pages, such as deleting funds, people and households, sermons, songs, requests, bulletins and tags, and cancelling pledges. This applies on every page, not only the new ones." },
      { type: "fix", text: "In dark mode, the words on red buttons, such as Delete in a confirmation, are now light and easy to read. They were dark on red." },
    ],
  },
  {
    id: "2026-10-18-6",
    date: "2026-10-18",
    title: "Scheduler settings",
    requiresFlag: "auto_scheduler",
    items: [
      { type: "improvement", text: "The auto-scheduler's settings now open in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with a Done button in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-18-5",
    date: "2026-10-18",
    title: "Custom field changes stay with the right church",
    items: [
      { type: "fix", text: "Adding, editing, or removing a custom field on Settings, and saving a person's custom field answers, is recorded for the church you made the change in, even if you switch churches before your connection returns." },
    ],
  },
  {
    id: "2026-10-18-4",
    date: "2026-10-18",
    title: "Adding a service element",
    items: [
      { type: "improvement", text: "Adding an item to an order of service now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Add item and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-18-3",
    date: "2026-10-18",
    title: "Uploading an HR document",
    items: [
      { type: "improvement", text: "Uploading a policy, handbook, or form on the HR Documents page now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Upload document and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-18-2",
    date: "2026-10-18",
    title: "Campus changes stay with the right church",
    items: [
      { type: "fix", text: "Adding, editing, renaming, or removing a campus on Settings > Locations is saved for the church you made the change in, even if you switch churches before your connection returns." },
    ],
  },
  {
    id: "2026-10-18-1",
    date: "2026-10-18",
    title: "Creating a consent form",
    items: [
      { type: "improvement", text: "Creating a new consent form on the Documents page now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Create link and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-18",
    date: "2026-10-18",
    title: "Writing and editing help articles",
    items: [
      { type: "improvement", text: "Creating or editing a help article now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Save and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-17-16",
    date: "2026-10-17",
    title: "Building a smart list",
    items: [
      { type: "improvement", text: "Creating or editing a smart list on the People page now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Save and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-17-15",
    date: "2026-10-17",
    title: "Saved settings stay saved on screen",
    items: [
      { type: "fix", text: "A setting you just saved no longer flips back to its old value for a moment if the page was refreshing in the background when you pressed Save." },
    ],
  },
  {
    id: "2026-10-17-14",
    date: "2026-10-17",
    title: "A report's schedule shows what you saved",
    items: [
      { type: "fix", text: "If you leave a report while its email schedule is still saving and come back before the save finishes, the report now waits for it, then shows the schedule as it was saved. Before, it could show the default settings instead, and pressing Save or Pause from there replaced the send time and frequency you had just saved." },
    ],
  },
  {
    id: "2026-10-17-13",
    date: "2026-10-17",
    title: "A clearer page for each event",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Each event now opens on a page with its name, date, time and place at the top, one main button for what you will most likely do next (check people in while the event is on, or print the attendance report afterwards), and tabs for Details, Roster, Registrations and Activity. Each tab has its own link, so Back, bookmarks and shared links open the right tab." },
      { type: "improvement", text: "Check-in and registration totals sit beside the tabs and open the matching list. Anything that needs attention, such as registration being full or children's pickup details that couldn't be checked, shows as a notice under the event's name." },
      { type: "improvement", text: "If the roster or the registrations can't load, only that part of the page says so and offers Try again; the rest of the event stays usable. Removing a registration now asks you to confirm first." },
      { type: "improvement", text: "Once an event has ended, its roster shows who checked in without check-in boxes, so attendance isn't added or removed by accident afterwards. Someone listed on the roster without a check-in time shows as not checked in, rather than with a box that couldn't check them in. Add registration appears once you save registrations as Enabled, and each save, check-in or removal is sent once, however quickly its button is pressed. If you go Back or Forward to another event, or leave for another page, while something is still saving, it finishes for the event you started it on, and the page you are looking at now stays as it is." },
      { type: "fix", text: "Saving an event's registration settings no longer turns a capacity of 0 into no limit, so an event you closed to new registrations stays closed. Spots remaining now count only people marked Going, the same way sign-ups are limited." },
      { type: "fix", text: "Delete event is available to your church's owners and admins in Forge, the people who can delete an event, and no longer looks available to someone whose delete would be refused." },
      { type: "fix", text: "Someone with a read-only role, such as Auditor, sees an event's roster, registrations and settings without the buttons to change them, even if they are also an owner or admin, so a change Forge would refuse never looks like it worked, such as a removed registration that was still there. The Delete event menu says the read-only role is why it isn't available." },
      { type: "fix", text: "If your access changes while Add attendee or Add registration is open, saving pauses and says why, and screen readers announce the reason. An open confirmation to remove a registration or delete the event closes, tells you why, and returns you to where you were, so nothing is changed that your role no longer allows." },
      { type: "fix", text: "Someone whose role can't view the calendar sees a message that they don't have access instead of the event, even for an event on a calendar shared with all members. If that access is removed while the event is open, its details, roster and registrations are hidden right away." },
      { type: "fix", text: "Your church's owners and admins can always open an event and delete it, even without another role assigned to them. Its roster, registrations and activity show only to staff roles that can see everyone's check-ins and registrations. Someone whose only role is Member or Volunteer, even an owner or admin, sees a message on those tabs instead of a list that would show only their own household and look complete." },
      { type: "fix", text: "Staff whose access is limited to certain ministries keep every check-in and registration control on an event, including its registration settings. Its Roster and Activity tabs, and the Checked in column on Registrations, say that check-ins in other ministries' rooms aren't shown, so a partial roster never looks complete. The check-in totals, the roster export and the report, which would leave those check-ins out without saying so, aren't shown to them." },
      { type: "fix", text: "When you go Back or Forward to another event, or return to one you just left, its registration settings show that event's own saved values straight away, and nothing you had open or typed on the earlier visit carries over, so a save can't change the wrong event. Anything still saving for that event, such as a check-in or its settings, shows as saving when you come back, and can't be sent again until it finishes, so no one is checked in twice and an older save can't replace a newer one." },
      { type: "fix", text: "Someone with a read-only role no longer sees the pickup code check for a children's event, which their role can't use." },
      { type: "fix", text: "If Forge can't check your access while an event is open, check-in, registration changes, the pickup code check and Delete event pause, and an open Add attendee or Add registration says why, until Try again succeeds. Nothing is changed in the meantime." },
      { type: "fix", text: "If you use a keyboard or a screen reader, closing anything that opens over an event, such as Add attendee, Add registration, a pickup code or a confirmation, puts you back on the button that opened it, so you don't lose your place. After you remove a registration you land on the next one in the list." },
    ],
  },
  {
    id: "2026-10-17-9",
    date: "2026-10-17",
    title: "Recording membership on a person's profile",
    items: [
      { type: "improvement", text: "On a person's profile, recording or editing a membership now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Save and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-17-8",
    date: "2026-10-17",
    title: "Adding and editing addresses on a person's profile",
    items: [
      { type: "improvement", text: "On a person's profile, adding or editing an address now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Save and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-17-6",
    date: "2026-10-17",
    title: "Reports open as their own page, with Results, Schedule and History",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Opening a report now shows its name, category and the period it covers at the top, with Run report as the main button and exports beside it. The report itself, its delivery schedule and a history of who exported or shared it each have their own tab, so you can link straight to a report's schedule or history." },
      { type: "improvement", text: "A report's saved views and the health of its schedule sit beside the results, and if a scheduled send didn't go out, the report says so at the top with a link to fix it. Saving a schedule keeps you on the Schedule tab and confirms it was saved. A Year-End Giving Statements schedule saved while you're viewing an earlier year keeps sending that year's statements, even in later years when that year is no longer in the year list." },
      { type: "improvement", text: "Printing Year-End Giving Statements prints the year you're viewing. Whether you use Print preview, your browser's Print command or a PDF export, the printout shows the dates that year covers, such as January 1 – December 31, 2023, instead of the report's ordinary date range. Reports that don't use a date range, such as Member Directory, no longer print one. A Trial Balance printout shows the date its balances are as of, such as September 30, 2026, matching the report and its PDF." },
      { type: "fix", text: "A printout's \"Generated\" time is now when you printed it. A 2023 statement printed today no longer says it was generated on December 31, 2023." },
      { type: "improvement", text: "Printing a report from its Schedule or History tab now includes the report's chart, just as printing from Results does." },
      { type: "fix", text: "Printing a report with your browser's Print command now puts its heading at the top of the page: your church's name, the report's title, the period it covers and when it was printed." },
      { type: "fix", text: "Run report is now unavailable while a report is still working out which dates it covers, or couldn't work them out, just as the export buttons are, so a run is never recorded against the wrong dates." },
      { type: "fix", text: "If your browser blocks the window Year-End Giving Statements open in to print, you're now told how to allow pop-ups, and the report's History no longer lists a print that never opened." },
      { type: "fix", text: "A share link copied while you're viewing an earlier statement year, such as 2023, now opens that year for the person you send it to, instead of last year's statements." },
      { type: "fix", text: "Pausing a report's schedule, or changing when it sends, no longer changes the filters it sends with. A Year-End Giving Statements schedule set up for 2023 keeps sending 2023's statements, even if you open the report showing a different year. Only filters you change on the Results tab before saving are saved with the schedule, including any you set back to where they started: changing Fund to Missions and back to All, or the statement year to 2024 and back to last year, saves All or last year." },
      { type: "fix", text: "A schedule's next send time now changes to Pending as soon as that time passes, instead of staying on the page until midnight." },
      { type: "fix", text: "Run report from the Schedule or History tab now records the run only once the report has loaded again. If it can't load, the Results tab says so and nothing is recorded. If you change the report's filters before it finishes loading, that run isn't recorded either, and the report tells you so; select Run report again to record one with the new filters. A report that couldn't load no longer says when it was generated, and its printout doesn't either." },
      { type: "fix", text: "Running, exporting, printing or sharing Year-End Giving Statements for an earlier year, such as 2023, now records that year with the run, so the report's records show which year's statements were produced, not last year's." },
      { type: "fix", text: "Exporting, printing or sharing a report is now recorded in the report's history, with who did it and when. These weren't being saved before, so the history stayed empty. If one can't be recorded, you're told so, and the export itself still works." },
      { type: "improvement", text: "A report's History tab, and the number beside it, are for admins and auditors, who can see your church's audit trail. Anyone else is told who can see it, instead of being shown an empty history. History lists only exports, prints and shared links, not report runs or older activity, and its number counts just those." },
      { type: "improvement", text: "A report's history records exports by your church's own members. If someone from Forge's staff who isn't a member of your church exports one of your reports, that export isn't listed there yet, and they're told so when they export and on the History tab." },
      { type: "fix", text: "An empty report opened with a filter it doesn't use, such as a fund on the Trial Balance, a date range on the Member Directory, or a date range on the Balance Sheet, which is always as of today, now shows the report's usual empty message instead of \"No results match these filters\"." },
      { type: "fix", text: "While a report with no results checks whether your church has started using the feature it relies on, such as giving or check-in, Run report and the export buttons wait, so nothing is recorded or downloaded before the report knows what to show." },
      { type: "fix", text: "Reports whose figures are always as of today, whatever date range you pick, now show the date they're as of at the top of the report, on printouts and in PDFs, instead of the date range you picked. These are the Balance Sheet, Statement of Activity, General Ledger Detail, Cash Flow Statement, Fund Balance Summary, Giving Gap Report and Volunteer Schedule." },
      { type: "fix", text: "The \"Generated\" time at the top of a report now shows when the results on your screen were loaded, so you can tell how current they are." },
    ],
  },
  {
    id: "2026-10-17-4",
    date: "2026-10-17",
    title: "Logging asset maintenance",
    items: [
      { type: "improvement", text: "Logging or editing maintenance on an asset now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Save and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-17-3",
    date: "2026-10-17",
    title: "Adding and editing training records on a person's profile",
    items: [
      { type: "improvement", text: "On a person's profile, adding or editing a training record now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Save and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-17-2",
    date: "2026-10-17",
    title: "Adding and editing background checks on a person's profile",
    items: [
      { type: "improvement", text: "On a person's profile, adding or editing a background check now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Save and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-17-1",
    date: "2026-10-17",
    title: "Adding and editing education on a person's profile",
    items: [
      { type: "improvement", text: "On a person's profile, adding or editing an education entry now opens in the same side panel used across the rest of Forge (a bottom panel on a phone) instead of a pop-up window, with Save and Cancel in the same place as everywhere else." },
    ],
  },
  {
    id: "2026-10-16-104",
    date: "2026-10-16",
    title: "One settings menu across Settings, and a clearer Church profile",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Settings pages now show the same settings menu as Finance settings, beside the page and grouped into your organization, each area of Forge, and administration. On smaller screens it becomes a \"Settings section\" list at the top. The menu only shows pages you are allowed to open." },
      { type: "improvement", text: "Church profile (your church's name, logo, time zone, public bulletin links, support email, branding and giving statement text) is laid out more clearly, and nothing is saved until you choose Save changes in the bar at the bottom of the page. Discard puts back what was saved, and asks first if you changed more than one thing. If a change can't be saved, it stays on screen with a note beside it so you can try again." },
      { type: "improvement", text: "If you open a settings page your role doesn't include, the page now tells you which permission you need and who can give it to you, instead of sending you to an error page." },
      { type: "fix", text: "A link to one setting, such as the background-check vendor in Kids check-in, now scrolls to that setting once the page has finished loading." },
      { type: "fix", text: "If you're offline when you save your Church profile, choose a background-check vendor, send or cancel a team invitation, or send someone a password reset link, and you switch to another church before your connection comes back, nothing changes in the church you switched to. A change that was already on its way is saved to the church you made it in. If you saved several Church profile changes at once, any that hadn't been sent yet are listed in a message, so you can switch back to that church and make them again. While that save waits, you can still save or discard changes in the church you switched to." },
      { type: "improvement", text: "My preferences is no longer listed in the menu beside your church's settings pages, which now holds only your church's settings. You can still open it from Starter task archive in your profile menu, and your notification settings still open from the settings button in your notifications." },
    ],
  },
  {
    id: "2026-10-16-103",
    date: "2026-10-16",
    title: "Bank connections settings, laid out like the other settings pages",
    items: [
      { type: "improvement", text: "Finance settings → Bank connections now has the same layout as the other settings pages, with the settings menu beside it. Your Stripe bank feeds, your bank accounts and your archived accounts each have their own section, and Add Bank Account sits at the top of the bank accounts section." },
      { type: "improvement", text: "To rename a bank account, type the new name in its box, then choose Save changes in the bar at the bottom of the page. Discard puts the saved names back, and asks first if you changed more than one. If a name can't be saved, the page tells you why next to that box and keeps what you typed." },
      { type: "improvement", text: "Disconnecting a Stripe bank feed now asks you to confirm first and explains what happens, and archiving a bank account offers Undo for a few seconds afterwards." },
      { type: "fix", text: "If your bank accounts or Stripe feed status can't be loaded, Bank connections now says so and offers Try again, instead of looking as if you had no accounts. If a later refresh fails, what already loaded stays on screen, with a note and Try again." },
      { type: "fix", text: "If your role doesn't give you access to bank connections, the page now says which permission you need and who can grant it, instead of showing empty lists." },
      { type: "fix", text: "Auditors can open Bank connections to review your bank accounts. With auditor or other read-only access, account names can't be edited here, and Archive and Reactivate stay unavailable as before." },
      { type: "improvement", text: "Each section of Bank connections now loads on its own, so your Stripe bank feeds, with Sync now and Disconnect, are ready to use while your bank accounts are still loading." },
      { type: "fix", text: "If your access becomes read-only while you are renaming an account, its box goes back to the saved name and Save changes is no longer offered, since the new name couldn't be saved." },
      { type: "fix", text: "When you switch to another organization, Bank connections starts fresh for it: a \"Saved\" note, an open Add Bank Account window, or a save, sync or archive still running in the organization you left no longer shows there or holds up its buttons." },
    ],
  },
  {
    id: "2026-10-16-99",
    date: "2026-10-16",
    title: "Accounts payable settings save when you choose",
    items: [
      { type: "improvement", text: "The Accounts payable settings page now works like the other settings pages. Turning \"Require attachment for approval\" on or off takes effect only when you choose Save changes in the bar that appears at the bottom of the page, and Discard puts the saved setting back. If a change can't be saved, the page says why right under the setting and keeps your edit so you can try again. If you switch to another organization before saving, your change is discarded and the saved setting stays as it was." },
      { type: "fix", text: "If your role doesn't let you see Accounts payable settings, the page now says so and who can give you access, instead of showing a setting that may not match your organization's, and the settings menu no longer lists the page for you. Auditors and other view-only roles can see the setting but can't change it, and the page says so." },
    ],
  },
  {
    id: "2026-10-16-98",
    date: "2026-10-16",
    title: "Expense allocations on one settings page",
    items: [
      { type: "improvement", text: "The Expense allocations page in Finance settings now shows your allocation rules and your allocation runs together, one above the other, instead of in two tabs. Adding, editing and deleting rules, running an allocation and voiding a run work as before." },
      { type: "improvement", text: "When you add or edit a rule, or choose a period for a run, and something required is missing, the window now says what is missing next to that field. Save stays available while you fill in a rule, so you can press it at any point to see what is still needed, and if the program, management and fundraising percentages don't add up to 100%, it says so next to them instead of saving. If a rule can't be saved, the window stays open with everything you entered so you can try again." },
      { type: "improvement", text: "If your rules or runs can't be loaded, the page now says so in that section with a Try again button, instead of showing an empty list. If a list that already loaded can't be refreshed, it stays on screen with all its buttons, and a note says it may not show your latest changes." },
      { type: "improvement", text: "After you add or edit a rule, the rules section shows \"Saved\" and that rule's row is briefly highlighted. When you run an allocation or void a run, that run's row is highlighted the same way." },
    ],
  },
  {
    id: "2026-10-16-95",
    date: "2026-10-16",
    title: "A clearer QuickBooks settings page",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "The QuickBooks settings page now sits beside the settings menu, with the connection, any refunds or voids still waiting on QuickBooks, the sync schedule and recent syncs each in their own section. It opens from Settings → Integrations or from Finance, and takes you back to whichever one you came from." },
      { type: "improvement", text: "Changing how often QuickBooks syncs, or whether admins are emailed about failed syncs, no longer saves through a separate button. A bar appears at the bottom naming what you changed, with Save changes and Discard. If you changed both, Discard asks first, and if a save doesn't go through your changes stay on screen so you can try again." },
      { type: "improvement", text: "Disconnecting QuickBooks now asks you to confirm first, because Forge gives up its access to your QuickBooks company and an admin has to sign in again to reconnect." },
      { type: "fix", text: "If Forge can't load whether QuickBooks is connected, or its recent syncs, the page now says so and offers Try again, instead of showing QuickBooks as not connected or the sync history as empty." },
    ],
  },
  {
    id: "2026-10-16-88",
    date: "2026-10-16",
    title: "Editing a gift now has its own page",
    items: [
      { type: "improvement", text: "Editing a gift now opens on its own page instead of a pop-up, so a gift split across many funds is easy to review and change. The fields and what you can edit are the same as before, and you come back to the gift when you save." },
    ],
  },
  {
    id: "2026-10-16-87",
    date: "2026-10-16",
    title: "A tidier Finance integrations page",
    items: [
      { type: "improvement", text: "The Integrations page in Finance settings now shows Stripe, QuickBooks Online and the planned payroll provider as matching cards, each with its connection status at the top. Every button to connect, check status or disconnect works as before." },
      { type: "fix", text: "If Forge can't load your Stripe or QuickBooks Online connection, the page now says so and offers Try again, instead of showing it as not connected." },
    ],
  },
  {
    id: "2026-10-16-85",
    date: "2026-10-16",
    title: "Scan-to-email settings are now in Finance settings",
    items: [
      { type: "improvement", text: "Scan-to-email intake, where you set up the email address that turns forwarded vendor invoices into draft bills, now sits with the other Finance settings and has its own place in the settings menu. Old links to it still open it." },
      { type: "improvement", text: "Changing the address, turning emailed bills on or off, or choosing who can send bills no longer saves the moment you click. A bar at the bottom of the page lists what you changed, with Save changes and Discard, so you can make several changes and save them together. If a save doesn't go through, your changes stay on screen so you can try again." },
      { type: "improvement", text: "The log of emails received now appears on the same page, below the settings, with the same status filter and refresh button." },
      { type: "fix", text: "If your role lets you see Scan-to-email intake but not change it, such as Auditor, the page shows your address, who can send bills and the email log with nothing to edit, and says which permission changing them needs and who can give it to you. If no address has been set up yet, you see a note saying so rather than a setup form you can't use. If your role can't see Scan-to-email intake, the page says so and who can give you access, and the settings menu no longer lists it for you." },
    ],
  },
  {
    id: "2026-10-16-84",
    date: "2026-10-16",
    title: "A clearer page for each bank deposit",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Each bank deposit now has its own page with the deposit's date, status and source at the top, and separate tabs for the gifts in the deposit, adding counted gifts, and matching it to your bank feed. Each tab has its own link, so you can share or bookmark the exact view you need." },
      { type: "improvement", text: "When a deposit's gifts don't add up to the amount you expected, a notice at the top says by how much and takes you straight to the fix. The deposit's totals and its progress, from created to reconciled with the bank, are shown alongside." },
      { type: "fix", text: "A gift that was voided, refunded or failed, or hasn't finished processing, stays listed on its deposit but no longer counts toward the deposit's total or its difference. It is marked as not counted, and the totals beside the deposit say how many gifts aren't counted. If a reconciled or synced deposit doesn't add up, for example after a gift in it was refunded, the notice says what to do instead: record the accounting adjustment for that change (in a journal entry for a synced deposit), or, for a reconciled deposit, reopen its reconciliation in Banking to change its gifts." },
    ],
  },
  {
    id: "2026-10-16-81",
    date: "2026-10-16",
    title: "A clearer Approval policies page",
    items: [
      { type: "improvement", text: "Finance settings → Approval policies now looks like the other settings pages, with the settings menu beside it. Each policy says in plain words which transactions it sends for approval and which are approved automatically. When two policies cover the same transaction, only one of them is used, and the other policy now says so by name." },
      { type: "improvement", text: "Turning a policy on or off no longer saves straight away. A bar at the bottom of the page lists your unsaved changes, with Save changes and Discard, so nothing changes until you save. If a change can't be saved, it stays in place so you can try again." },
      { type: "improvement", text: "Adding a policy, approving or rejecting requests that are waiting, and looking back at past requests now all sit on the one page instead of separate tabs. Past requests appear when you choose Show history." },
    ],
  },
  {
    id: "2026-10-16-69",
    date: "2026-10-16",
    title: "A tidier Recurring rules page in Finance settings",
    items: [
      { type: "improvement", text: "Finance settings → Recurring rules now matches the other settings pages: one card lists recurring journals and recurring expenses, each with a short note on what it does and a button to the page where you add, pause, change or delete those schedules. The settings menu stays beside it, with Recurring rules highlighted." },
    ],
  },
  {
    id: "2026-10-16-64",
    date: "2026-10-16",
    title: "Pop-up forms scroll as one piece on small screens",
    items: [
      { type: "improvement", text: "On a phone or a short window, pop-up forms such as adding a note, inviting a team member, or creating a request now scroll as one piece when they are taller than the screen, so more of the form fits. Their buttons stay pinned at the bottom, and moving between fields with the keyboard never leaves the field you are on hidden behind them." },
    ],
  },
  {
    id: "2026-10-16-59",
    date: "2026-10-16",
    title: "A clearer page for each fund",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Each fund now opens to a cleaner page: its name, type, restriction and status at the top, then Overview, Contributions, Allocations and Activity tabs you can bookmark or share. The totals on the Overview open the gifts behind them, and the fund's history reads as a simple timeline." },
      { type: "improvement", text: "Going back to Funds from a fund, using the Funds link at the top, returns you to the list just as you left it, with the same view, filters, search and sort. Editing a fund brings you back to the fund when you save or cancel." },
    ],
  },
  {
    id: "2026-10-16-43",
    date: "2026-10-16",
    title: "A clearer page for each care request",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Each pastoral care request now opens on a page laid out like a person's record: the request, who it's for, its stage, priority and confidentiality at the top, Overview and Notes tabs underneath, and the person and care team alongside. The Care link at the top of the page takes you back to the Care list just as you left it." },
      { type: "improvement", text: "Add note is now the main button at the top of a request, and its notes show as a timeline, newest first, with private notes clearly marked. Each tab has its own link, so you can share or bookmark a request's notes directly." },
      { type: "improvement", text: "Closing a request now asks for the reason in its own window and explains what closing does before you confirm. If part of a request can't load, only that part says so, with a Try again button." },
    ],
  },
  {
    id: "2026-10-16-42",
    date: "2026-10-16",
    title: "One settings menu on Finance settings",
    items: [
      { type: "improvement", text: "Finance settings pages now show one settings menu beside the page, grouped into your organization, each area of Forge (with every Finance settings page), and administration, so you can move to any settings page from there. On smaller screens it becomes a \"Settings section\" list at the top. The menu only shows pages you are allowed to open." },
      { type: "fix", text: "In light mode, the bar at the bottom of a settings page that lists your unsaved changes, such as on Contribution defaults, now shows its message in a readable color instead of dark text on the dark bar." },
    ],
  },
  {
    id: "2026-10-16-37",
    date: "2026-10-16",
    title: "A clearer page for each pledge",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Opening a pledge now shows who made it, its campaign and its status at the top, with one Send reminder button. The Details tab shows how much was pledged, how much has been received and what is still outstanding, with a short note under each number explaining where it comes from, followed by the pledge's terms, commitment card answers and notes." },
      { type: "improvement", text: "The gifts matched to a pledge have their own Activity tab, newest first, each linking to the gift. The donor's profile, whether the pledge is on schedule, its fund and campaign, and its latest gifts sit beside the details." },
      { type: "improvement", text: "Anything on a pledge that needs your attention, such as allocations that still need setting up before gifts count toward it, now appears at the top of the page." },
      { type: "improvement", text: "Under More actions, Edit pledge takes you to the Pledges list with that pledge's editor already open, and Cancel pledge cancels it and takes you back to the list, with an Undo in the notice that follows." },
    ],
  },
  {
    id: "2026-10-16-34",
    date: "2026-10-16",
    title: "A clearer page for each gift",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Opening a gift now shows the amount, who gave it and its status at the top, with anything that needs you listed right underneath, such as a gift waiting for review or one that isn't assigned to a fund yet." },
      { type: "improvement", text: "A gift's details and its change history are now separate tabs you can link to directly. The contributor, any linked pledge and the gift's batch sit beside them, with a link straight to the batch report." },
      { type: "improvement", text: "The main thing to do next with a gift sits at the top right. After you delete a gift you land back on the contributions list you came from, with your filters still in place." },
    ],
  },
  {
    id: "2026-10-16-2",
    date: "2026-10-16",
    title: "Easier-to-read selection bar on lists",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "fix", text: "On lists such as People, the bar that appears when you select rows now shows its \"3 people selected\" text in a readable color and keeps Email, Add to group and More on one line instead of dropping them to a second row." },
    ],
  },
  {
    id: "2026-10-16-1",
    date: "2026-10-16",
    title: "Softer dark mode for household and People notices",
    items: [
      { type: "improvement", text: "In dark mode, the amber and blue notice banners on household giving units, the household page, and the People list now use softer, easier-to-read tints instead of heavy coloured blocks. Light mode is unchanged." },
      { type: "improvement", text: "In dark mode, the Stripe integration notices, conditional contribution tracking, and barrier assessment badges now use the same softer status tints, so green, amber, blue, and red states are easier to read. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-16",
    date: "2026-10-16",
    title: "Choose which events appear on your website",
    items: [
      { type: "fix", text: "Events can now be shown on your public events page and on a church website connected to Forge. Turn on \"Show on public calendar and website\" when you create an event, or on the event's Details tab for an existing one. Only events on a calendar with Public visibility can be listed, so members-only and private events always stay private. Repeating events can't be listed yet." },
      { type: "fix", text: "Events whose calendar isn't Public, and repeating events, no longer appear on your public events page or website, even if an earlier update had marked them as listed." },
      { type: "fix", text: "Switching between reports no longer carries one report's email schedule over to the next, so saving a schedule on one report can't overwrite another report's schedule." },
      { type: "fix", text: "If you switch churches while a fund's settings are saving, they're saved to the church you made the change in, never to the one you switched to. If it was a fund you had just created, its settings aren't saved and you see a \"Failed to update settings\" message instead. Either way, one church's fund settings can't be changed by mistake from another's." },
      { type: "fix", text: "On a pledge's page, \"Delete Pledge\" is now \"Cancel Pledge\" and really cancels the pledge, with a notice to undo it, instead of only taking you back to the list. Bookkeepers no longer see a \"Send Reminder\" button they aren't allowed to use." },
      { type: "fix", text: "If you switch churches while your church's name, logo, branding colors, statement text, or support contact email is saving, a change that was already on its way is saved to the church you made it in, and nothing changes in the church you switched to. A name you typed for one church is never saved to another. This keeps one church's profile from being changed by mistake from another's." },
      { type: "fix", text: "A bank deposit with more than 1,000 gifts now loads every one of them, so its list of gifts and its totals are complete, instead of stopping at the first 1,000 without saying so." },
    ],
  },
  {
    id: "2026-10-15-11",
    date: "2026-10-15",
    title: "Finance dashboard shows what you still owe on bills",
    items: [
      { type: "fix", text: "On the Finance dashboard, the \"AP Outstanding\" card now counts only what is still owed on posted bills, so a bill you have fully paid no longer inflates the total. Clicking the card opens the Accounts Payable bills list showing just the bills with money still owed, matching the number on the card." },
      { type: "fix", text: "The Payable side of the dashboard's Payment aging card now counts the same way, showing only what is still owed on posted bills: bills you have paid, and bills approved but not yet posted, no longer appear there. How overdue each bill is now counts from today's date in your church's time zone. The Accounts Payable bills list and the list of bills to pay in a payment run also load every bill now, instead of stopping at the first 1,000 without saying so." },
    ],
  },
  {
    id: "2026-10-15-10",
    date: "2026-10-15",
    title: "Softer dark mode for Conditional Contributions and status badges",
    items: [
      { type: "improvement", text: "In dark mode, the Conditional Contributions page, its barrier checklist, neutral status badges on finance screens, and the \"Install Forge\" prompt now use the same warm charcoal as the rest of the app instead of a cool blue-gray. Light mode is unchanged." },
      { type: "improvement", text: "In dark mode, green, amber, red and blue status badges and indicators on finance and scheduling screens now use softer, easier-to-read tints instead of heavy coloured blocks. Light mode is unchanged." },
      { type: "improvement", text: "The same softer dark-mode tints now apply to the fund type and account type labels in finance setup and the chart of accounts, role badges on people, service-plan and billing status labels, and the giving setup checklist. Light mode is unchanged." },
    ],
  },
  {
    id: "2026-10-15-9",
    date: "2026-10-15",
    title: "Old giving widget retired",
    items: [
      { type: "fix", text: "The older embeddable giving widget no longer accepts gifts, because it recorded them without taking a payment. Visitors who try it are told to use your church's giving page, which charges the card and records the gift only once the payment goes through." },
    ],
  },
  {
    id: "2026-10-15-7",
    date: "2026-10-15",
    title: "Public group signups work again",
    items: [
      { type: "fix", text: "People can sign up for an open group from your public groups page again. They are added to the group, visitors who join are marked as active members, and someone already in your directory is matched to their existing record instead of being added twice." },
    ],
  },
  {
    id: "2026-10-15-6",
    date: "2026-10-15",
    title: "Full groups stop taking public signups",
    items: [
      { type: "fix", text: "When a group has a member limit, the public signup form no longer adds people past it. Once the group is full, new signups are turned away instead of quietly going over the limit." },
    ],
  },
  {
    id: "2026-10-15-5",
    date: "2026-10-15",
    title: "Warmer dark mode for the Today banner",
    items: [
      { type: "improvement", text: "In dark mode, the dark banner at the top of the Today page now uses the same warm charcoal as the rest of the app instead of a cool navy blue. Light mode looks nearly the same." },
    ],
  },
  {
    id: "2026-10-15-4",
    date: "2026-10-15",
    title: "See finance entries that are waiting to post",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "The finance queue can now show only entries that have not been posted to the books yet. A \"Not yet posted\" chip shows when the filter is on and removes it with one click. View totals are hidden while it is on, so they never include entries that are already posted." },
    ],
  },
  {
    id: "2026-10-15-3",
    date: "2026-10-15",
    title: "Find the bills you still owe",
    items: [
      { type: "improvement", text: "On Accounts Payable, the Status filter on the Bills list now has an \"Open balance\" choice that shows only bills with money still owed, and the amount shown is what is still owed on each one." },
    ],
  },
  {
    id: "2026-10-15-2",
    date: "2026-10-15",
    title: "Every Accounts Receivable section is back",
    items: [
      { type: "fix", text: "Accounts Receivable now has a tab for each section, so Unapplied receipts, Recurring invoices, Payment plans and Fee schedules can be opened again, and you can return to the Overview from anywhere. The page you are on is kept in the link, so Back and bookmarks work." },
    ],
  },
  {
    id: "2026-10-15-1",
    date: "2026-10-15",
    title: "Warmer dark mode for the welcome banner",
    items: [
      { type: "improvement", text: "In dark mode, the welcome banner on the first-run setup page now uses the same warm charcoal as the rest of the app instead of a cool blue." },
    ],
  },
  {
    id: "2026-10-14-7",
    date: "2026-10-14",
    title: "Open assigned tasks directly from notifications",
    items: [
      { type: "fix", text: "Task notifications now take volunteers straight to their assigned task, including completed tasks, and ask you to choose the right organization when needed." },
    ],
  },
  {
    id: "2026-10-14-6",
    date: "2026-10-14",
    title: "Warmer dark mode for the dashboard momentum card",
    items: [
      { type: "improvement", text: "In dark mode, the dashboard's momentum card now uses the same warm charcoal as the rest of the app instead of a cool blue." },
    ],
  },
  {
    id: "2026-10-14-5",
    date: "2026-10-14",
    title: "Time off balances now follow approved leave",
    items: [
      { type: "fix", text: "Approving a vacation, sick or personal time-off request now counts those days as used on the person's balance, and denying, changing or deleting the request gives them back, so the remaining balances and the low-balance alert on Time Off reflect real leave." },
    ],
  },
  {
    id: "2026-10-14-4",
    date: "2026-10-14",
    title: "Clearer entry for saved giving batches",
    requiresFlag: "saved_batch_workspace_v1",
    items: [
      { type: "improvement", text: "Adding gifts to a saved batch keeps entry controls aligned and payment methods on one row, with comfortable wrapping on smaller screens." },
    ],
  },
  {
    id: "2026-10-14-3",
    date: "2026-10-14",
    title: "Clearer batch giving entry",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "Batch giving keeps entry controls aligned and payment methods on one row, with comfortable wrapping on smaller screens." },
    ],
  },
  {
    id: "2026-10-14-1",
    date: "2026-10-14",
    title: "Monthly and quarterly pledges are discounted on their real payment schedule",
    items: [
      { type: "fix", text: "When you record the present value of a pledge, a monthly or quarterly pledge is now discounted over its actual monthly or quarterly payments instead of as one year-end payment, so the discount is no longer overstated. Weekly and biweekly pledges use monthly payments. Pledges you already discounted are not changed." },
    ],
  },
  {
    id: "2026-10-13-12",
    date: "2026-10-13",
    title: "Keep your public pages light in dark mode",
    items: [
      { type: "feature", text: "In Settings, admins can now choose to always show the giving, join, groups and public form pages in light, for every visitor, even those whose device is set to dark mode." },
      { type: "improvement", text: "Visitors who return to the giving, join or groups pages of a church that keeps its public pages light no longer see a dark flash while the page loads." },
    ],
  },
  {
    id: "2026-10-13-10",
    date: "2026-10-13",
    title: "Easier gift amounts and clearer daily giving",
    items: [
      { type: "fix", text: "Online gift amounts are easier to clear, edit and paste, with a decimal keyboard on mobile." },
      { type: "improvement", text: "Online giving goes straight to payment review without an extra payment-selection control." },
      { type: "improvement", text: "Gifts today includes submitted online gifts and staff-entered gifts, with processing payments shown separately from confirmed giving." },
      { type: "improvement", text: "New cash and check batches record gifts already received, using your existing posting choice. Review and reconcile the books at monthly close." },
    ],
  },
  {
    id: "2026-10-13-9",
    date: "2026-10-13",
    title: "Email preview stays light in dark mode",
    items: [
      { type: "improvement", text: "In dark mode, the email template preview now sits on a light page, the way the email will look in your recipients' inboxes." },
    ],
  },
  {
    id: "2026-10-13-8",
    date: "2026-10-13",
    title: "Bulletin preview stays light in dark mode",
    items: [
      { type: "improvement", text: "In dark mode, the bulletin builder now shows your bulletin as a light page, the way it will look when printed or shared." },
    ],
  },
  {
    id: "2026-10-13-7",
    date: "2026-10-13",
    title: "Report pack preview stays light in dark mode",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "improvement", text: "In dark mode, the Report Pack editor now shows your pack as a light page, the way it will look when printed or shared, and its menus match." },
    ],
  },
  {
    id: "2026-10-13-6",
    date: "2026-10-13",
    title: "The \"page not found\" screen works better with screen readers",
    items: [
      { type: "improvement", text: "The \"page not found\" screen now marks its content as the main part of the page, so screen readers and the skip-to-content shortcut can jump straight to it." },
    ],
  },
  {
    id: "2026-10-13-5",
    date: "2026-10-13",
    title: "Attendance events work with a keyboard",
    items: [
      { type: "improvement", text: "On the Attendance page, you can now open an event from the list with the keyboard (Tab to the event, then press Enter)." },
    ],
  },
  {
    id: "2026-10-13-4",
    date: "2026-10-13",
    title: "Accurate dark mode preview in Settings",
    items: [
      { type: "fix", text: "In Settings, the Dark and System choices now preview the same warm dark look you get after choosing them, instead of an older blue-gray one." },
    ],
  },
  {
    id: "2026-10-13-3",
    date: "2026-10-13",
    title: "Contribution details work with a keyboard",
    items: [
      { type: "improvement", text: "On a contribution's detail page, the linked person and linked pledge cards can now be opened with the keyboard (Tab to the card, then press Enter)." },
    ],
  },
  {
    id: "2026-10-13-2",
    date: "2026-10-13",
    title: "Announcements work with a keyboard",
    items: [
      { type: "improvement", text: "On the member Announcements page, you can now open and close an announcement with the keyboard or a screen reader. Each title announces whether it is expanded." },
    ],
  },
  {
    id: "2026-10-13-1",
    date: "2026-10-13",
    title: "Readable emails in dark mode",
    items: [
      { type: "fix", text: "Account emails keep their text and confirmation codes readable in dark mode. Email template previews match the sent layout and let you check both light and dark appearances without sending a message." },
    ],
  },
  // Unreleased source change; publication remains held.
  {
    id: "2026-10-13",
    date: "2026-10-13",
    title: "Check-in kiosk stays bright in dark mode",
    items: [
      { type: "improvement", text: "The self-service check-in kiosk now always shows in light mode, even if the tablet or your account uses dark mode, so it stays easy to read in a bright lobby and matches the labels it prints." },
    ],
  },
  {
    id: "2026-10-12-9",
    date: "2026-10-12",
    title: "Clearer run-of-service history",
    requiresFlag: "run_of_service_reliability",
    items: [
      { type: "fix", text: "Run-of-service history is now listed by when each entry was recorded rather than by its number, which keeps it in a sensible order when two people edit the same service at once. If a history entry can't be saved, you now see a warning instead of losing it silently." },
    ],
  },
  {
    id: "2026-10-12-8",
    date: "2026-10-12",
    title: "Record card and ACH gifts in giving batches",
    items: [
      { type: "fix", text: "New giving batches now include Card and ACH for recording gifts already received through another giving system, alongside Cash and Check." },
    ],
  },
  {
    id: "2026-10-12-7",
    date: "2026-10-12",
    title: "Save opening balances together",
    items: [
      { type: "fix", text: "Accounting setup now saves all opening balances together, so a failed save does not leave an incomplete journal." },
    ],
  },
  {
    id: "2026-10-12-6",
    date: "2026-10-12",
    title: "Reliable pledge choices for offline gifts",
    items: [
      { type: "fix", text: "Recording an offline gift now checks every matching pledge for the gift date and fund, and lets you retry if pledge choices cannot load." },
    ],
  },
  {
    id: "2026-10-12-5",
    date: "2026-10-12",
    title: "Clearer errors on record pages",
    items: [
      { type: "fix", text: "Failed actions on record pages now show one error message and remain available to try again." },
    ],
  },
  {
    id: "2026-10-12-4",
    date: "2026-10-12",
    title: "Keep selected bulletin announcements during loading problems",
    items: [
      { type: "fix", text: "Bulletin autosave keeps previously saved announcements when the announcements list cannot be refreshed. Announcements you remove stay removed, and public sharing continues to show only public announcements." },
    ],
  },
  {
    id: "2026-10-12-2",
    date: "2026-10-12",
    title: "Live service items no longer get out of step between coordinators",
    items: [
      { type: "fix", text: "If two people change the same item while a service is live, Forge now tells the second person the item has already moved on instead of recording a step that never happened, so the service history and song usage reports stay accurate." },
    ],
  },
  {
    id: "2026-10-12-1",
    date: "2026-10-12",
    title: "Undoing a voided vendor payment now checks the bill first",
    items: [
      { type: "fix", text: "If you void a vendor payment, another payment is then applied to the same bill, and you try to Undo the void, Forge now stops you with a clear message instead of letting the bill be paid twice." },
    ],
  },
  {
    id: "2026-10-12",
    date: "2026-10-12",
    title: "Keyboard access, dark mode and current pledge choices",
    items: [
      { type: "improvement", text: "You can open the People CSV file picker with Enter or Space, and contact labels identify the right fields when several forms are open." },
      { type: "fix", text: "Selected items remain visible on older iPhones and Safari, and dark cards keep their clean appearance when you hover." },
      { type: "fix", text: "Pledge choices in giving entry now refresh after pledge changes and imports. Undo restores a cancelled pledge in its original organization and confirms that the restore succeeded." },
      { type: "improvement", text: "In Finance, you can now Tab to each journal result in the transaction dialog and press Enter or Space to choose it, without using a mouse." },
      { type: "improvement", text: "On several pages, including Contributions, Forms, Pastoral Care, Staff Today and Group details, clicking a label such as \"Compact rows\" or \"Required\" now switches its checkbox, toggle or field, and screen readers announce the pairing." },
      { type: "improvement", text: "Rows you can click to open, such as pledge payments, project tasks, the household link on a request and broadcast calendar days, can now be reached with Tab and opened with Enter or Space." },
    ],
  },
  {
    id: "2026-10-11-1",
    date: "2026-10-11",
    title: "A warmer, more consistent dark mode",
    items: [
      { type: "improvement", text: "Dark mode now uses one warm charcoal look everywhere. Pages no longer show two slightly different blacks side by side, and text, borders and the sidebar are easier to read." },
    ],
  },
  {
    id: "2026-10-11",
    date: "2026-10-11",
    title: "Clearer fund details in saved giving batches",
    requiresFlag: "saved_batch_workspace_v1",
    items: [
      { type: "improvement", text: "The fund picker for saved giving batches shows when restriction details are unavailable, so you can choose by fund name without relying on an inferred restriction label." },
    ],
  },
  {
    id: "2026-10-10-4",
    date: "2026-10-10",
    title: "Clarifying manual review after restarting Stripe setup",
    items: [
      { type: "fix", text: "Correction to our earlier Stripe update: Start over clears the connection in Forge and flags unused Express and Custom accounts for our team to review. Forge does not automatically close those accounts; any closure is handled manually in Stripe. Standard accounts continue to be cleared in Forge only." },
    ],
  },
  {
    id: "2026-10-10-3",
    date: "2026-10-10",
    title: "Work with saved giving batches in place",
    requiresFlag: "saved_batch_workspace_v1",
    items: [
      { type: "improvement", text: "Eligible saved giving batches now have a workspace on their report to change the date or service, edit a gift, and add more gifts. Changes require an audit reason, and entry drafts are saved locally for the current browser session." },
    ],
  },
  {
    id: "2026-10-10-2",
    date: "2026-10-10",
    title: "Clarifying how Pledges loads totals",
    items: [
      { type: "fix", text: "Correction to our earlier Pledges update: the list includes every matching pledge, including lists with more than 1,000 records. Totals and progress load separately, so they may reflect changes made while the list is loading." },
    ],
  },
  {
    id: "2026-10-10-1",
    date: "2026-10-10",
    title: "Use the project timeline with the keyboard",
    items: [
      { type: "improvement", text: "On the project timeline you can now Tab to a task bar or milestone and press Enter or Space to open it, and screen readers announce each one by name." },
    ],
  },
  {
    id: "2026-10-09-6",
    date: "2026-10-09",
    title: "Safer Start over for Stripe setup",
    items: [
      { type: "fix", text: "When you use Start over on a Stripe connection that never took a payment, Forge now automatically closes the old unused Stripe account (Express and Custom accounts) a minute or so later, and keeps retrying if Stripe is briefly unavailable. In rare cases where the account was finished or used in the meantime it is left alone and our team is alerted. Standard accounts are cleared in Forge only, as before." },
    ],
  },
  {
    id: "2026-10-09-5",
    date: "2026-10-09",
    title: "More reliable project choices",
    items: [
      { type: "fix", text: "Project choices stay consistent when someone adds or removes a project while the list is loading." },
    ],
  },
  {
    id: "2026-10-09-4",
    date: "2026-10-09",
    title: "Change a team member's access with the keyboard",
    items: [
      { type: "improvement", text: "In Settings, the Change Access Level choices (Admin and Member) can now be reached with Tab and picked with Space or the arrow keys, and screen readers announce them as a single choice group." },
    ],
  },
  {
    id: "2026-10-09-3",
    date: "2026-10-09",
    title: "Use the calendar without a mouse",
    items: [
      { type: "improvement", text: "On the calendar, you can now Tab to an event, an all-day event or a day's agenda item and press Enter or Space to open it." },
    ],
  },
  {
    id: "2026-10-09-2",
    date: "2026-10-09",
    title: "Truer module colors in dark mode",
    items: [
      { type: "fix", text: "In dark mode, module icons now use their own colors instead of a mismatched set. The Home icon is gold again instead of green, and each module's icon is easier to read against the dark background." },
    ],
  },
  {
    id: "2026-10-09-1",
    date: "2026-10-09",
    title: "Easier-to-use table page sizes",
    items: [
      { type: "fix", text: "Screen readers now announce what the rows-per-page menu under data tables is for, such as on the Organizations page." },
    ],
  },
  {
    id: "2026-10-09",
    date: "2026-10-09",
    title: "Clearer empty gift tables",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "fix", text: "Screen readers can read the empty state in batch gift tables, including when a search has no matches." },
    ],
  },
  {
    id: "2026-10-08-9",
    date: "2026-10-08",
    title: "Sign-in fits iPhones with a notch",
    items: [
      { type: "fix", text: "On iPhones with a notch or Dynamic Island, the Forge logo on the sign-in and two-step verification screens no longer slides under the top of the screen." },
    ],
  },
  {
    id: "2026-10-08-8",
    date: "2026-10-08",
    title: "Find gift details from a batch report",
    items: [
      { type: "improvement", text: "Batch reports show a clear View gift link for each contribution, so you can find its details and the correction actions available to you." },
      { type: "fix", text: "Gift corrections refresh the batch report’s rows and totals. Choosing another batch keeps your selected interface when you open its gift details." },
    ],
  },
  {
    id: "2026-10-08-7",
    date: "2026-10-08",
    title: "One Forge logo everywhere",
    items: [
      { type: "improvement", text: "The sign-in page, browser tab, home-screen icon and phone app now all show the same Forge mark, with matching lettering on the sign-in page. Older shield and placeholder icons are gone." },
    ],
  },
  {
    id: "2026-10-08-6",
    date: "2026-10-08",
    title: "Find older posting history",
    items: [
      { type: "fix", text: "Browse older postings, including failed attempts, beyond the newest fifty. Refresh history to return to the latest postings." },
    ],
  },
  {
    id: "2026-10-08-4",
    date: "2026-10-08",
    title: "Clearer fund splits in batch giving",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "fix", text: "Each fund split keeps its fund name visible when a pledge match is available. Pledge fields explain whether a match is optional or required and show the available or selected campaign." },
      { type: "improvement", text: "On a phone, the unsaved batch’s date and service stay labeled and readable. Open on a larger screen to change them or enter gifts." },
    ],
  },
  {
    id: "2026-10-08-3",
    date: "2026-10-08",
    title: "Easier pledge entry with a keyboard",
    items: [
      { type: "improvement", text: "Use Enter or Space to choose a pledge import file. When adding a new person to a pledge, the name field receives focus so you can continue typing." },
      { type: "fix", text: "Person pickers include the selected person’s name for screen readers." },
    ],
  },
  {
    id: "2026-10-08-2",
    date: "2026-10-08",
    title: "Consistent finance lists and safer group changes",
    items: [
      { type: "fix", text: "Deposits and Pledges lists now include every matching record, even beyond 1,000, with counts and totals from the same complete view when records change during loading." },
      { type: "fix", text: "Groups used to limit a role can no longer be deleted until that role is moved to another scope in Settings. Forge explains which step is needed before you continue." },
      { type: "fix", text: "Group choices in role settings now load as one complete list, so changes made while it loads do not produce a partly updated list." },
    ],
  },
  {
    id: "2026-10-08-1",
    date: "2026-10-08",
    title: "Email domain setup checks the right access",
    items: [{ type: "fix", text: "Organization owners and admins can add email domains without a server access error being mistaken for missing permissions. If an access check is temporarily unavailable, domain setup asks you to retry." }],
  },
  {
    id: "2026-10-08",
    date: "2026-10-08",
    title: "Search shortcuts work with the keyboard",
    items: [{ type: "improvement", text: "In search, recent searches and the Go to Page shortcuts can now be chosen with the arrow keys and Enter, and screen readers announce them as options. The remove button on a recent search now has a name too, and pressing Delete removes the highlighted recent search from the keyboard. Hovering or arrowing onto a Go to Page shortcut shows its preview." }],
  },
  {
    id: "2026-10-07",
    date: "2026-10-07",
    title: "Finance permissions follow your organization",
    items: [{
      type: "fix",
      text: "Automatic fund releases now check your finance permissions in the organization you're working in.",
    }],
  },
  {
    id: "2026-10-06-16",
    date: "2026-10-06",
    title: "Scope a role to a small group",
    items: [{
      type: "fix",
      text: "When you limit a role to a group in Settings, you now pick from your small groups (like Young Adults) instead of family groups.",
    }],
  },
  {
    id: "2026-10-06-15",
    date: "2026-10-06",
    title: "Visitor cards can finish processing on retry",
    items: [{
      type: "fix",
      text: "If follow-up processing is temporarily unavailable, a Visitor card keeps its saved response and lets the visitor retry instead of reporting completion too soon.",
    }],
  },
  {
    id: "2026-10-06-13",
    date: "2026-10-06",
    title: "Find contributors and pledges in larger lists",
    items: [{
      type: "fix",
      text: "Batch giving entry includes contributors and pledge choices beyond the first 1,000 records for your organization. If contributors cannot load, batch entry shows the error and lets you retry.",
    }],
  },
  {
    id: "2026-10-06-12",
    date: "2026-10-06",
    title: "Visitor card details stay in the response",
    items: [{
      type: "fix",
      text: "Visitor cards sent from existing website widgets keep their additional answers, and staff can review and export them alongside the configured fields.",
    }],
  },
  {
    id: "2026-10-06-11",
    date: "2026-10-06",
    title: "Add users in a panel that fits your screen",
    items: [{
      type: "improvement",
      text: "Adding a user now opens in a side panel on larger screens and a bottom sheet on phones, with the create and cancel controls kept in view.",
    }],
  },
  {
    id: "2026-10-06-10",
    date: "2026-10-06",
    title: "Organizations fit tablets",
    items: [{
      type: "improvement",
      text: "On the Organizations list, tablets keep name, email and phone in view, and add household and date added as there is room. On phones each organization is a card, and a Sort menu replaces the column headers you can no longer tap.",
    }],
  },
  {
    id: "2026-10-06-9",
    date: "2026-10-06",
    title: "Summary alerts are easier to follow",
    items: [{
      type: "fix",
      text: "Warning and error summaries now use clearer colors in dark mode, making their labels, counts, trends and retry links easier to read. Loading summaries also have properly named status indicators for assistive technology.",
    }],
  },
  {
    id: "2026-10-06-8",
    date: "2026-10-06",
    title: "Find a document and keep your place",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "improvement", text: "Search your loaded consent forms, filter their acknowledgement status, and use Back to return to the same document and view. Counts make clear when they cover only the most recent 200 documents." }],
  },
  {
    id: "2026-10-06-7",
    date: "2026-10-06",
    title: "Groups fit tablets and phones",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{
      type: "improvement",
      text: "On the Groups list, tablets now keep the table and show the columns that fit, while phones switch to easy-to-tap cards. Sorting is a menu whenever a column is hidden.",
    }],
  },
  {
    id: "2026-10-06-6",
    date: "2026-10-06",
    title: "Phone header actions stay within reach",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{
      type: "fix",
      text: "On phones, the new layout keeps navigation, organization switching, search, notifications and your profile easy to tap. Quick Add and What's new are available in More actions without overlapping the other controls.",
    }],
  },
  {
    id: "2026-10-06-5",
    date: "2026-10-06",
    title: "Visitor connect responses stay connected",
    items: [{ type: "fix", text: "Visitor connect cards now create or match a person when configured, keep response totals current, and show staff when a response needs attention. Administrators can choose a group for in-app response alerts." }],
  },
  {
    id: "2026-10-06-4",
    date: "2026-10-06",
    title: "Keep your place while browsing",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "improvement", text: "On list and record pages, breadcrumbs move into the top bar on larger screens. Search stays one click away, while phone pages keep their familiar navigation." }]
  },
  {
    id: "2026-10-06-3",
    date: "2026-10-06",
    title: "Posting uses the selected rows on screen",
    items: [{
      type: "fix",
      text: "When the posting queue refreshes, Post to GL now uses exactly the selected rows still on screen. Rows that have left the queue are excluded immediately, keeping the selection count and the submitted entries in agreement. The Select all checkbox now appears in the queue, and changing pages clears the selection before you can post.",
    }],
  },
  {
    id: "2026-10-06-2",
    date: "2026-10-06",
    title: "Keep batch entry aligned with your access",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "fix", text: "When your access scope in the current organization changes, batch entry closes the previous unsaved session and reloads its available donors, funds, and pledges." }],
  },
  {
    id: "2026-10-06-1",
    date: "2026-10-06",
    title: "Enter giving batches with fewer clicks",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{
      type: "improvement",
      text: "Enter cash and check gifts on a full page, use the keyboard to add gifts, correct unsaved rows, and see running totals. Gifts remain unsaved until you choose Save batch, with a warning before you leave unfinished work.",
    }, {
      type: "fix",
      text: "If contributors cannot load, retry the search before adding a new person. Batch entry waits for the lookup to finish, helping avoid duplicate people. After it loads, you can use Enter on New person.",
    }],
  },
  {
    id: "2026-10-05-17",
    date: "2026-10-05",
    title: "Notifications stay connected while browsing",
    items: [
      { type: "fix", text: "The Notifications page uses the app's shared live connection, so opening the page keeps updates working without showing an error." },
      { type: "improvement", text: "Theme controls are easier to tap on phones and tablets." },
    ],
  },
  {
    id: "2026-10-05-16",
    date: "2026-10-05",
    title: "Detail panels match everywhere",
    items: [
      {
        type: "improvement",
        text: "Pledges, Services and report-row detail panels now use the same quick view: a right-hand panel on wider screens and a bottom sheet on phones. People and Funds use the same quick view in their small-screen card layouts, so you can inspect details without leaving the list.",
      },
    ],
  },
  {
    id: "2026-10-05-15",
    date: "2026-10-05",
    title: "Enter a batch of gifts with your keyboard",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{
      type: "feature",
      text: "Enter gifts on a full page with running totals and inline corrections. Your entries stay unsaved until you choose Save. On a phone, review the entries and open a larger screen to continue entering gifts.",
    }],
  },
  {
    id: "2026-10-05-14",
    date: "2026-10-05",
    title: "Tag several people at once",
    items: [
      {
        type: "fix",
        text: "On the People page, select people and choose Tag to apply a tag to all of them at once. Before, the Tag button did nothing. The Send SMS shortcuts, which could not reach anyone, are gone; use Email to message people."
      }
    ]
  },
  {
    id: "2026-10-05-13",
    date: "2026-10-05",
    title: "Addresses in staff contact information",
    items: [{
      type: "fix",
      text: "See saved profile, household, and personal addresses in a person's Contact Information, with each source clearly labeled. Member directory privacy choices still apply in the member portal and no longer appear as restrictions on staff records.",
    }],
  },
  {
    id: "2026-10-05-12",
    date: "2026-10-05",
    title: "Related details get an About heading on phones",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "improvement", text: "On phones, the related information that sits beside a record on larger screens now appears below it under an About heading, so it is easy to find." }],
  },
  {
    id: "2026-10-05-11",
    date: "2026-10-05",
    title: "Sermon details open in the same panel",
    items: [{
      type: "improvement",
      text: "On Sermons, adding or editing a sermon now opens in the same side panel used across the app, and as a bottom sheet on phones, so the sermon list stays visible behind it. Saving and cancelling work as before.",
    }],
  },
  {
    id: "2026-10-05-10",
    date: "2026-10-05",
    title: "Request, task and gift details open in the same panel",
    items: [{
      type: "improvement",
      text: "On Requests, Tasks, Calendar tasks and Giving, record details now open in the same side panel used across the app, and as a bottom sheet on phones. Buttons at the bottom, like Open request or a status change, work as before and close the panel. The link to the full page is at the bottom, and closing the panel puts you back where you were.",
    }],
  },
  {
    id: "2026-10-05-9",
    date: "2026-10-05",
    title: "People filter keyboard focus",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "fix", text: "People filters keep keyboard focus when the phone sheet closes on resize and when Save Segment closes, so the next action remains reachable." }],
  },
  {
    id: "2026-10-05-8",
    date: "2026-10-05",
    title: "People filters reflect your selected tag",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{
      type: "fix",
      text: "On phones, People shows your selected tag in the filter count and lets you remove it from the filter sheet. The Filters button appears only when there is something to use or clear.",
    }],
  },
  {
    id: "2026-10-05-7",
    date: "2026-10-05",
    title: "Volunteer assignment details match other panels",
    items: [{
      type: "improvement",
      text: "On Services, a volunteer's assignment details now open in the same side panel used across the app, and as a bottom sheet on phones. Sending a message about the assignment works as before.",
    }],
  },
  {
    id: "2026-10-05-6",
    date: "2026-10-05",
    title: "Document filters show their active count",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "fix", text: "On phones, the Documents Filters button now shows when a status filter is active. Search stays separate from that count." }],
  },
  {
    id: "2026-10-05-4",
    date: "2026-10-05",
    title: "Finance record details open the same way",
    items: [{
      type: "improvement",
      text: "On Banking and the Journal viewer, record details now open in the same side panel used across the app, and as a bottom sheet on phones. The link to the full page is at the bottom, and closing the panel puts you back on the row you came from.",
    }],
  },
  {
    id: "2026-10-05-3",
    date: "2026-10-05",
    title: "List filters fit on phones",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{
      type: "improvement",
      text: "On phones, list pages keep the search box in view and tuck filters and sorting behind one Filters button, which shows how many filters are on. Everything opens in a roomy sheet with large, easy-to-tap controls.",
    }],
  },
  {
    id: "2026-10-05-2",
    date: "2026-10-05",
    title: "Keep organization details in view",
    items: [{
      type: "fix",
      text: "The Organizations list keeps phone numbers visible on tablets and shows the added date on phone cards. The Sort menu shows your current choice and direction, with a larger target on smaller screens.",
    }],
  },
  {
    id: "2026-10-05",
    date: "2026-10-05",
    title: "Pledge list shows only the giver's pledges",
    items: [
      {
        type: "fix",
        text: "When you record an offline gift to a fund with pledges, the pledge list now shows only the pledges that belong to the person giving. Before, it listed everyone's pledges under the same name, which made it hard to pick the right one and could block saving the gift."
      }
    ]
  },
  {
    id: "2026-10-04-16",
    date: "2026-10-04",
    title: "Reliable list filters during quick changes",
    items: [
      {
        type: "fix",
        text: "Quickly changing a search or filter keeps your latest choice. Showing services that need attention clears the needs-volunteers filter, even when you switch immediately."
      }
    ]
  },
  {
    id: "2026-10-04-12",
    date: "2026-10-04",
    title: "Notification lists recover after updates",
    items: [{
      type: "fix",
      text: "When a notification page disappears after an update or filter change, the list returns to a valid page and clears its old selection instead of showing an error or stale rows."
    }]
  },
  {
    id: "2026-10-04-9",
    date: "2026-10-04",
    title: "Knowledge Base links work again",
    items: [{
      type: "fix",
      text: "Open Forge's guides and how-tos from Help & Support, the mobile More menu, the footer, or search. These links now take you to the working documentation site.",
    }],
  },
  {
    id: "2026-10-04-2",
    date: "2026-10-04",
    title: "Room bookings stay current",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      { type: "fix", text: "An outdated booking page automatically returns to the last available page. Upcoming and past bookings stay current as time passes, and open booking forms close when access is no longer available." }
    ]
  },
  {
    id: "2026-10-03-24",
    date: "2026-10-03",
    title: "Journal entries show their own currency",
    items: [{
      type: "fix",
      text: "The Journal Viewer now shows each entry's amounts in the currency it was recorded in, in both the Total debit column and the line details. Before, an entry recorded in Canadian dollars or another currency appeared with a dollar sign as if it were US dollars.",
    }],
  },
  {
    id: "2026-10-03-21",
    date: "2026-10-03",
    title: "See who is new this month",
    items: [
      {
        type: "feature",
        text: "On the Everyone list, \"New this month\" shows the people added since the first of the month."
      }
    ]
  },
  {
    id: "2026-10-03-20",
    date: "2026-10-03",
    title: "Clearer keyboard focus in dark mode",
    items: [
      {
        type: "improvement",
        text: "In dark mode, keyboard focus now uses a light blue outline, with a light outline around gold buttons, so the next action is easier to see."
      }
    ]
  },
  {
    id: "2026-10-03-18",
    date: "2026-10-03",
    title: "Smoother overview navigation",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "fix",
        text: "Overview totals open their detail pages without reloading, and keyboard focus moves to the page heading."
      }
    ]
  },
  {
    id: "2026-10-03-17",
    date: "2026-10-03",
    title: "Reliable task links and request recovery",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "fix",
        text: "Opening a task's project or request from the task keeps you in the refreshed layout."
      },
      {
        type: "fix",
        text: "If a change to several requests stops partway, you can still undo the changes that were saved."
      }
    ]
  },
  {
    id: "2026-10-03-16",
    date: "2026-10-03",
    title: "Recover completed bank donations",
    items: [{
      type: "fix",
      text: "Completed bank donations can now recover their payment and processing-fee details when Stripe uses a bank-payment reference, helping stalled contributions finish posting correctly.",
    }],
  },
  {
    id: "2026-10-03-15",
    date: "2026-10-03",
    title: "Clearer dark pages and easier tablet navigation",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "fix", text: "Refreshed admin pages keep a clear dark background so headings and breadcrumbs stay readable. Module navigation buttons are easier to tap on tablets, with a clear outline when you use the keyboard." }],
  },
  {
    id: "2026-10-03-14", date: "2026-10-03",
    title: "A workspace for building report packs",
    requiresFlag: "ui_uplift", requiresValue: "enabled",
    items: [{ type: "improvement", text: "Build report packs with a section palette, document preview and settings panel. Changes save automatically, with a clear status when a save needs attention." }],
  },
  {
    id: "2026-10-03-13",
    date: "2026-10-03",
    title: "Room bookings with a clearer list",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "improvement", text: "The refreshed room bookings page lets you search by title or room, switch between upcoming, past and all bookings, and page through the full matching list. Your view stays in the web address, typing searches together, and each booking shows its recorded owner when available. Calendar stays selected while you browse rooms. Read errors and permission denials have clear messages, and phone cards keep booking descriptions visible. Booking, cancellation and deletion keep their existing steps." }],
  },
  {
    id: "2026-10-03-12",
    date: "2026-10-03",
    title: "A clearer song library",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "improvement", text: "The refreshed Song library shows the full filtered count and lets you move through every page, search songs and artists, filter by tempo, and include archived songs. The web address keeps your list position when you return. Song details open beside the list on larger screens and in a bottom sheet on phones, with keyboard theme choices and entered details kept when a save needs another try." }],
  },
  {
    id: "2026-10-03-11",
    date: "2026-10-03",
    title: "Compact spacing in refreshed admin pages",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "fix",
        text: "Choosing Compact display density now reduces the space around refreshed admin pages too. Member and volunteer pages keep their existing spacing.",
      },
    ],
  },
  {
    id: "2026-10-03-10",
    date: "2026-10-03",
    title: "Clearer directory settings",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "improvement", text: "The refreshed directory settings page explains how directory access affects all members, shows your unsaved change with Save and Discard, and lets you retry a section that could not load. Invitations keep their familiar steps." }],
  },
  {
    id: "2026-10-03-9",
    date: "2026-10-03",
    title: "Keep your place while pages load",
    items: [
      {
        type: "fix",
        text: "Within the same organization, open quick-add panels and unfinished drafts now stay open when service, people, bulletin, volunteer-hours, and finance pages finish loading. Keyboard focus in those panels stays where you left it too.",
      },
    ],
  },
  {
    id: "2026-10-03-8",
    date: "2026-10-03",
    title: "Event templates you can search and tidy up",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "The refreshed Event templates page lists every template with its type, length and what it includes, and you can search by name, filter by event type and sort by any column. Admins can create, edit and duplicate a template from its row, and delete one or several at once after confirming. Your search, filter, sort, page and selection stay in the page's web address, so Back returns to the same list. If templates don't load, the page says so with Try again instead of showing an empty list. The page works on phones and in dark mode.",
      },
    ],
  },
  {
    id: "2026-10-03-7",
    date: "2026-10-03",
    title: "Roomier pages on larger screens",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "In the refreshed layout, the space around page content now adjusts to your screen: a little wider on tablets and wider again on laptops and desktops, so phones keep as much room for your content as possible.",
      },
    ],
  },
  {
    id: "2026-10-03-6",
    date: "2026-10-03",
    title: "Notifications you can sort through",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "The refreshed Notifications page has tabs for all, unread and read notifications and a filter by type, and every page of results now matches your choice instead of filtering only the notifications already shown. Open a notification from anywhere on its row, mark it read or unread, select several to mark read together (with Undo), or mark everything read at once. Your tab, type and page stay in the page's web address, so Back returns to the same list. If notifications don't load, the page says so with Try again instead of showing an empty list. The page works on phones and in dark mode.",
      },
    ],
  },
  {
    id: "2026-10-03-5",
    date: "2026-10-03",
    title: "Easier-to-see keyboard focus",
    items: [
      {
        type: "improvement",
        text: "When you move through a page with the Tab key, the outline around the selected button or link is now easier to see: a clear blue in the light theme, and dark on the gold buttons. In the dark theme it stays amber.",
      },
    ],
  },
  {
    id: "2026-10-03-2",
    date: "2026-10-03",
    title: "A simpler way back on phones",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "On a phone, pages built on the new record layout now show a single \u2039 back link to their list instead of the full trail of page names, and it is easy to tap. Other record pages will follow as they move to that layout.",
      },
    ],
  },
  {
    id: "2026-10-03-1",
    date: "2026-10-03",
    title: "Filters open in a side panel on tablets",
    items: [
      {
        type: "improvement",
        text: "On tablets, the Filters button on lists, tables and reports now opens a panel on the right instead of a sheet from the bottom, so you can still see the list while you filter. Phones keep the bottom sheet.",
      },
    ],
  },
  {
    id: "2026-10-03",
    date: "2026-10-03",
    title: "Admin pages open from links and refreshes",
    items: [
      {
        type: "fix",
        text: "Opening an administrator or finance page straight from a bookmark, a link, or a browser refresh no longer shows \"Access restricted\" to people who have access. Pages such as Integrations, Finance Queue, Deposits, Compliance Queue and Group Management now open the same way they do when you reach them through the menu.",
      },
    ],
  },
  {
    id: "2026-10-02-11",
    date: "2026-10-02",
    title: "Personal saved views stay personal",
    items: [
      {
        type: "fix",
        text: "Saved views you mark as Personal are now visible only to you. Before, other staff in your organization could see them in the Saved views menu on pages like Contributions, Projects and Tasks. Views shared with your organization are unchanged.",
      },
    ],
  },
  {
    id: "2026-10-02-10",
    date: "2026-10-02",
    title: "Engagement scoring under People settings",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "Engagement scoring now lives with the other People settings. Old links and bookmarks to it keep working and bring your filters along.",
      },
    ],
  },
  {
    id: "2026-10-02-9",
    date: "2026-10-02",
    title: "Payroll export now downloads a real CSV",
    requiresFlag: "payroll_integration",
    items: [
      {
        type: "fix",
        text: "Export payroll file on the Time Off page now downloads a spreadsheet-ready CSV that your payroll provider can import, instead of a technical file you could not open.",
      },
    ],
  },
  {
    id: "2026-10-02-8",
    date: "2026-10-02",
    title: "Roomier side panels, and bottom sheets on phones",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "Quick view panels are now wider and can show action buttons pinned at the bottom. On phones they open as a bottom sheet that stays within the visible screen and clears the home bar.",
      },
    ],
  },
  {
    // Re-numbered from 2026-10-02-2, which dev uses for the contributions list entry (#7205).
    id: "2026-10-02-7",
    date: "2026-10-02",
    title: "Find and review service connections",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "The refreshed Integrations page lets organization administrators search providers, filter connection status and category, save personal views, and export connection summaries. Open a provider record or Quick view, return with your list choices intact, and use the existing setup and recovery controls. If a refresh fails, the last verified connections stay visible while connection changes wait for a successful retry. The collection works on phones and in dark mode.",
      },
    ],
  },
  {
    id: "2026-10-02-6",
    date: "2026-10-02",
    title: "Find tasks and keep your place",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "Search and filter tasks, open a task to work on it, then go Back to the same list or Board view. Save your preferred views, highlight matching rows, and see due dates on the timeline. Undo recent task edits without replacing later changes.",
      },
    ],
  },
  {
    id: "2026-10-02-5",
    date: "2026-10-02",
    title: "People settings in one place",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "Directory settings now live under People, with a Settings section in the People navigation, so administrators can find them without leaving the module.",
      },
    ],
  },
  {
    // Re-numbered from 2026-10-02-1, which dev uses for the Trial Balance drill-down entry (#7173).
    id: "2026-10-02-4",
    date: "2026-10-02",
    title: "Find and organize announcements",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{
      type: "improvement",
      text: "The refreshed announcement list lets you search updates, switch status views, save your own filters, preview records, and export matching announcements. Your list settings return when you go Back, and saving a new announcement opens its record. The list works on phones and in dark mode.",
    }],
  },
  {
    // Re-numbered from 2026-10-02, which dev uses for the online-giving retry entry (#7166).
    id: "2026-10-02-3",
    date: "2026-10-02",
    title: "A clearer general ledger",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "The refreshed general ledger keeps descriptions and fund labels readable as you resize your window. Switch status views to hide voids and reversals, open a journal entry directly, and return to the same filters. When your access allows, export or print every matching line across all pages, with fund setup warnings kept visible.",
      },
    ],
  },
  {
    id: "2026-10-02-2",
    date: "2026-10-02",
    title: "A clearer contributions list",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{ type: "improvement", text: "Find gifts with a consistent contributions list, clear filters and a selection bar for group actions. Returning from a gift restores your search, sorting and page. Check numbers, comments and linked records remain available, and newly saved views stay active. Gift entry, exports and giving statements remain close at hand." }],
  },
  {
    id: "2026-10-02-1",
    date: "2026-10-02",
    title: "Trial Balance links open the right journal entries",
    items: [{
      type: "fix",
      text: "Clicking an account on the Trial Balance, Balance Sheet, Statement of Activity or Functional Expenses now opens the Journal Viewer showing the entries behind that figure: posted entries only, through the report date (or within its period), and for the fund you clicked. A note above the list says what is applied, and one click shows every account again. Net assets that are calculated rather than recorded no longer link to an empty list.",
    }, {
      type: "fix",
      text: "The Trial Balance now follows its Date range filter. It shows balances as of the last day of the period you pick.",
    }],
  },
  {
    id: "2026-10-02",
    date: "2026-10-02",
    title: "Reliable retry for online giving",
    items: [{
      type: "fix",
      text: "Staff can retry a failed Stripe giving payment from Integrations without losing track of it. Forge keeps any payment that still needs attention visible for follow-up.",
    }],
  },
  {
    // Re-numbered from 2026-10-01-5, which dev uses for the pre-go-live cleanup entry.
    id: "2026-10-01-9",
    date: "2026-10-01",
    title: "Find and work through your finance queue",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "The refreshed finance queue lets you search across all items and switch status views. When your access allows, open contributions directly or select items to review, prepare for export, or retry sync after confirming. Retry keeps your list position. Filters and selection return when you go Back, and the queue works on phones and in dark mode.",
      },
    ],
  },
  {
    // Everyone: route focus and announcements apply to both the classic and refreshed layouts.
    // Re-numbered from 2026-10-01-4, which dev already uses for the fund-restrictions fix.
    id: "2026-10-01-8",
    date: "2026-10-01",
    title: "Clearer page changes for keyboard and screen-reader users",
    items: [
      {
        type: "improvement",
        text: "When you open another page, Forge now moves keyboard focus to that page and politely announces that it has loaded, so you can continue without searching for where the new content begins.",
      },
    ],
  },
  {
    id: "2026-10-01-7",
    date: "2026-10-01",
    title: "Requests that are easier to review and follow up",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [{
      type: "improvement",
      text: "Care, operations, and finance requests now have clearer lists and record pages. Keep your filters when you return, review requests on your phone, and follow up through linked tasks.",
    }, {
      type: "fix",
      text: "In churches with more than 1,000 requests, the list no longer misses or repeats a request, or shows the wrong count, when someone adds or removes a request while it loads.",
    }],
  },
  {
    id: "2026-10-01-6",
    date: "2026-10-01",
    title: "Find and review fund compliance items",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "The refreshed compliance queue lets you search issues and funds, switch between severity views, and dismiss selected items after confirming. Your filters and selection return when you go Back from a fund, and the queue works on phones and in dark mode.",
      },
    ],
  },
  {
    id: "2026-10-01-4",
    date: "2026-10-01",
    title: "Fund restrictions stay intact during accounting setup",
    items: [{
      type: "fix",
      text: "Reopening accounting setup preserves each fund’s donor restriction or board designation. Restricted funds offer purpose, time, and permanent choices, and funds without a classification must be classified before saving.",
    }],
  },
  {
    id: "2026-10-01-3",
    date: "2026-10-01",
    title: "A clearer general ledger",
    items: [{ type: "fix", text: "The general ledger adapts to narrower windows, wraps long descriptions, and lets you filter funds and voids. Recorded contribution funds remain visible when accounting setup is missing, and running activity stays consistent when you sort or search." }],
  },
  {
    id: "2026-10-01-2",
    date: "2026-10-01",
    title: "Complete fund lists and up-to-date care access",
    items: [
      {
        type: "fix",
        text: "The Funds page now includes every fund, even in churches with more than 1,000 funds, so funds beyond the first thousand remain visible and searchable.",
      },
      {
        type: "fix",
        text: "People receive an approval notification when a group leader accepts their join request in the Member Portal or Group Finder, including leaders who only use the portal. Staff and church admins can open Group Finder from the notice; other members get a group link only while the member portal is available.",
      },
      {
        type: "fix",
        text: "Care lists refresh automatically while you keep them open, so confidential requests stop appearing when your care-team assignment ends.",
      },
    ],
  },
  {
    // Not gated: ending a live service is on both layouts.
    id: "2026-10-01",
    date: "2026-10-01",
    title: "Ending a live service ends the run you're looking at",
    items: [
      {
        type: "fix",
        text: "If someone else restarts a live service while your screen still shows the earlier run, pressing End no longer ends their new run. Forge tells you the service was restarted, refreshes the live view, and leaves it running so you can end it again if you meant to.",
      },
    ],
  },
  {
    id: "2026-09-30-9",
    date: "2026-09-30",
    title: "Trial Balance shows one balance per account",
    items: [
      {
        type: "fix",
        text: "The Trial Balance report now shows each account's net balance on one side, instead of listing all of its debits next to all of its credits. An account with $1,000 in and $400 out now reads $600, and the totals still match. The Reports screens that read the general ledger also no longer drop lines for churches with a very large number of entries.",
      },
    ],
  },
  {
    id: "2026-09-30-8",
    date: "2026-09-30",
    title: "The Delete button on the Posting Log explains itself",
    items: [
      {
        type: "improvement",
        text: "On the Posting Log, the Delete button used to sit greyed out with no explanation. It now says when it turns on (tick a gift that has not been posted) and where to go for entries that are already posted: delete or reverse the gift from Contributions.",
      },
    ],
  },
  {
    // Not gated: both fixes apply on either layout. Newest, above 2026-09-30-6.
    id: "2026-09-30-7",
    date: "2026-09-30",
    title: "Funds and project lists for larger churches",
    items: [
      {
        type: "fix",
        text: "The Funds page now reads every gift and campaign when it shows each fund's last gift and decides whether a fund can be deleted. In churches with more than 1,000 gift lines, some funds could show \"no gifts yet\" and be offered for deletion even though they had gifts.",
      },
      {
        type: "fix",
        text: "The list of projects you choose from when linking a task now includes every project, even past 1,000, and a project created while the list was loading no longer goes missing or shows up twice.",
      },
    ],
  },
  {
    id: "2026-09-30-6",
    date: "2026-09-30",
    title: "Project task counts stay up to date",
    items: [
      {
        type: "fix",
        text: "The task count on each project in the Projects list now updates on its own when a task changes somewhere else, such as another staff member finishing it or an automatic prep project adding tasks. A task that was deleted elsewhere drops off within a couple of minutes. Before, the list could show \"1/2 tasks complete\" for up to five minutes while the project itself showed something different.",
      },
    ],
  },
  {
    // Its own entry rather than a rewrite of 2026-09-28's item: that entry may ship to main before
    // this merges, and seen state is keyed by id, so edited text on a shipped id is never announced
    // (Codex P2 on #7134). Same flag as 2026-09-28, since it describes the same behavior.
    id: "2026-09-30-5",
    date: "2026-09-30",
    title: "Trying again after a slow connection",
    requiresFlag: "run_of_service_reliability",
    items: [
      {
        type: "fix",
        text: "If Forge gave up waiting while you were creating, duplicating, or copying last week's service, trying again with the same details now finds the service that already went through, even if you closed and reopened the window first. If you change the details before trying again, Forge creates the new service you asked for.",
      },
    ],
  },
  {
    // Its own entry rather than an item in 2026-09-28-1: that entry may ship to main before this
    // merges, and an item appended to a shipped entry is never announced (#7129).
    id: "2026-09-30-4",
    date: "2026-09-30",
    title: "Ending a live service finishes the current item",
    items: [
      {
        type: "fix",
        text: "Ending a live service now always marks the item that was on screen as Done, with how long it ran, even if your connection drops right as you end it. Before, that item could stay Active afterwards.",
      },
    ],
  },
  {
    // Not gated. Newest, above dev's 2026-09-30: a viewer who already saw that entry is only
    // told about an ungated item carried by a newer id (Codex P2 on #7115).
    id: "2026-09-30-2",
    date: "2026-09-30",
    title: "Hear back when you ask to join a group",
    items: [
      {
        type: "fix",
        text: "When a group leader approves or declines your request to join a group, you now get a notification telling you the answer. Before, the decision was silent and you had to check back to find out.",
      },
    ],
  },
  {
    // Everyone, not just ui_uplift: these pages keep their filters in the address on both layouts.
    // Newest, so the modal shows it. Was 2026-09-29-6, which dev's release-audit fixes entry (#7096)
    // took first; unused on dev and main.
    id: "2026-09-30",
    date: "2026-09-30",
    title: "Lists remember how you left them",
    items: [
      {
        type: "improvement",
        text: "People lists now keep how you left them in the page's web address: the People page's tab, sort and open row; the Households tab's search, sort and open household; the family groups search and sort; the smart list you had open and the Giving health column; Engagement scoring's search, sort and what-if weights; and Organizations' search, sort and page. A shared link opens the same view. Going Back, or following the People link on someone's page, also brings back where you had scrolled and the households you had selected, and deleting someone you opened from one of these lists returns you to it just as you left it.",
      },
      {
        type: "fix",
        text: "The People page no longer forgets your usual view (Members for a pastor, cards for a volunteer) when you come back to it with a search.",
      },
    ],
  },
  {
    // Not gated: fixes the UI uplift promotion shipped to everyone, flag on or off, that had no
    // item of their own (found in the release audit; see #7096). Newest, above 2026-09-29-5.
    id: "2026-09-29-6",
    date: "2026-09-29",
    title: "Fixes across People, Attendance and Finance",
    items: [
      {
        type: "fix",
        text: "The Attendance overview now updates as soon as you check someone in, save a roster or verify a pickup, instead of waiting until you reload the page.",
      },
      {
        type: "fix",
        text: "If the People list or someone's profile can't be loaded, Forge now says so and offers Try again, instead of showing \"No people yet\" or \"Person not found\".",
      },
      {
        type: "fix",
        text: "Visitors, Life events and Pledges now show every record, not just the first 1,000.",
      },
      {
        type: "fix",
        text: "A life event saved without a date is now kept as undated instead of failing to save.",
      },
      {
        type: "improvement",
        text: "Deleting a fund now waits until Forge has finished checking which gifts and campaigns use it, instead of letting you delete before that check has loaded.",
      },
      {
        type: "improvement",
        text: "Approving or rejecting a registration now needs permission to add people, and choosing Cancelled in Edit Pledge follows the same rule as Cancel pledge: only the Admin, Finance and Finance admin roles can cancel a pledge.",
      },
    ],
  },
  {
    // Not gated: the classic Pastoral Care form's fix reaches everyone. Newer than the open
    // ui_uplift rollup 2026-09-29-4, which it doesn't join (that card only shows once the flag is on).
    // Was 2026-09-28-1, then 2026-09-29-2: each dev merge of 2026-09-29 brought dev entries at the
    // lower ids (#7065, #7090), so this sits above dev's newest (2026-09-29).
    id: "2026-09-29-5",
    date: "2026-09-29",
    title: "Pastoral care requests save every time",
    items: [
      {
        type: "fix",
        text: "Creating a pastoral care request no longer fails when you keep the suggested type. The Type list now shows the kinds of care Forge actually records, including meals, visits and benevolence, so every request saves and shows its type. Confidential and sealed are offered only to a senior pastor, the one person who can open those requests once they are saved, instead of failing for everyone else.",
      },
      {
        type: "fix",
        text: "A care request's page shows the status list, Close Request and the note box only to pastoral staff, who can use them. Anyone else who can read the request sees it without controls that would fail.",
      },
    ],
  },
  {
    // Flag-gated (#6983): the ui_uplift rollup's next card. 2026-09-25 is on main (shipped), and a
    // shipped entry is never appended to ("seen" is keyed by id, so a new item there wouldn't
    // announce to anyone who already saw it). Later ui_uplift PRs add their items here until
    // this ships in turn. Newer than every id on dev and main (dev's newest: 2026-09-29) and
    // unused on both. Was 2026-09-28, then 2026-09-29-1, moved up by the 2026-09-29 dev merges
    // (#7065, #7090).
    id: "2026-09-29-4",
    date: "2026-09-29",
    title: "More refreshed pages",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "People pages now have a bar across the top with Everyone, Visitors, Groups, Care and Life events, so you can move between them without going back to a menu. Pages that don't have a place in the bar yet, like Organizations, Registration approvals, the Member directory and Membership pathways, are under More.",
      },
      {
        type: "improvement",
        text: "A person's profile puts their name, status and contact details at the top, with anything that needs your decision, like a household role to review, right underneath instead of hidden in a field. Household and family sit alongside the details, and empty sections tell you how to fill them in.",
      },
      {
        type: "improvement",
        text: "The People list and profiles now tell you exactly what's going on when there's nothing to show: whether your church hasn't added anyone yet, your search or filters hide everyone (with one click to clear them), something didn't load (your records are safe, and you can try again), or your role doesn't include access, with the permission named so an admin knows what to give you.",
      },
      {
        type: "improvement",
        text: "Visitors is now a list with a tab for each follow-up stage, from New Guest to Connected, each showing how many guests are in it. Click a guest to open their profile, move them to the next stage from their row, or add a note or send a welcome email from the row's menu. Every guest is counted and searchable, however many you have. Your tab and search stay in the page's web address, so Back and shared links return to the same list.",
      },
      {
        type: "improvement",
        text: "Life events is now a list with a tab for each kind of milestone, from baptisms to memberships, each showing how many you've recorded. Search by person, sort by person, event, date or officiant, and click a name to open their profile. Edit or delete an event from its row, or select several and delete them together. Your tab, search and sort stay in the page's web address, so Back and shared links return to the same list, and every event is counted however many you have.",
      },
      {
        type: "improvement",
        text: "Registration approvals opens on the requests waiting for your review, with tabs for approved, rejected and all requests, each showing how many there are. Search by name, email or phone, sort by name, status or date, and approve or reject from the row. An approved request links to the profile it created, and Copy registration link is one click from the top of the page. Your tab, search and sort stay in the page's web address, so Back and shared links return to the same list, and every request is counted however many you have.",
      },
      {
        type: "improvement",
        text: "Attendance is now a list of your events with how many people came, how many were first-time guests and how that compares with last time, with totals for the period on top. Choose the last 30 days, 90 days or 12 months, search by event, sort by date, name or attendance, and click an event to open it. Take attendance and Quick check-in are at the top of the page. Your tab, search, sort and period stay in the page's web address, so Back and shared links return to the same list, and every check-in is counted however many there are.",
      },
      {
        type: "improvement",
        text: "Finance → Contribution Defaults now tells you exactly what you've changed before you save, and Discard undoes those changes — nothing saves until you say so.",
      },
      {
        type: "improvement",
        text: "The trail above a list or profile, like People / Sarah Garcia, is shorter and clearer: it shows at most three steps, each step before the last is a link back, and the page you're on is no longer a link to itself. On a phone it's a single link back to the list.",
      },
      {
        type: "improvement",
        text: "Archiving a person now happens right away, with an Undo button for a few seconds if you clicked by mistake, instead of asking you to confirm first. Deleting a person, which can't be undone, still asks first, and now names the person and says what will be permanently removed.",
      },
      {
        type: "improvement",
        text: "Small groups and classes now live under People → Groups, as one list you can search and sort by name, ministry area, status or when a group was last updated. Tabs show every group, the ones open to join, the ones hidden from discovery and the ones with no ministry area, each with its count. Each row shows when and where the group meets; click a group's name to see its roster, or use its menu to edit or delete it, and select several to delete them together. Your tab, search and sort stay in the page's web address, so Back and shared links return to the same list, and every group is listed however many there are.",
      },
      {
        type: "improvement",
        text: "Pastoral care now lives under People → Care, as one list of the care requests you can see. It opens on the ones still open, with tabs for new requests, ones that need follow-up, urgent ones and closed ones, each with its count, and totals for open, urgent and follow-up at the top. Search by request or person, narrow it to one type of care, sort by date, urgency, status or confidentiality, and click a request to open it. A new request opens as soon as it's saved. Your tab, filters and sort stay in the page's web address, so Back and shared links return to the same list, and every request is listed however many there are.",
      },
      {
        type: "improvement",
        text: "Deposits now has its own place in Finance, next to Contributions, as one list of your bank deposits. Tabs show every deposit and the pending, reconciled and synced ones, each with its count, and totals for deposits, pending, reconciled and the amount that reached the bank sit at the top. Pick a deposit-date period, search by description, source or notes, sort by date, source, status or amount, and click a deposit to open it. A new deposit opens as soon as it's saved. Your tab, period, search and sort stay in the page's web address, so Back and shared links return to the same list. Anyone whose role can't see deposits is told so, instead of seeing an empty list.",
      },
      {
        type: "improvement",
        text: "Funds is now one list of your funds, with tabs for all, active, paused and archived funds, each with its count, and totals for each at the top. Narrow it to a kind of fund or a restriction, search by name or description, sort by name, type, usage, online giving or status, and click a fund to open it. Edit, pause, archive or delete a fund from its row, or select several to archive or delete together; funds with gifts or campaigns are archived rather than deleted. A new fund opens as soon as it's saved. Your tab, filter, search and sort stay in the page's web address, so Back and shared links return to the same list.",
      },
      {
        type: "improvement",
        text: "Pledges is now one list of every pledge, showing what was pledged, what's been received, what remains and whether it's on schedule. Tabs show every pledge and the active, behind-schedule, paused, completed and cancelled ones, each with its count, and totals for pledged, received, remaining and behind schedule sit at the top. Narrow it to one campaign, search by donor or campaign, sort by any column, and click a pledge to open it. Edit or cancel a pledge from its row, or select several and cancel them together. A new pledge opens as soon as it's saved. Campaigns are managed from Finance → Campaigns. Your tab, campaign, search and sort stay in the page's web address, so Back and shared links return to the same list. Pledges are for finance and admin roles; anyone else is told so.",
      },
      {
        type: "improvement",
        text: "Adding someone from the People list opens their new profile straight away. If you added just the person, with no email, household or ministry, and your role can delete people, an Undo lets you take it back in case you added them by mistake. Editing a profile keeps you on it, and the part you changed briefly highlights and says Saved. Deleting someone from their profile takes you back to the list you opened them from, with its filters and the rest of your selection, and exporting selected people clears the selection when it's done.",
      },
    ],
  },
  {
    // Not gated, so it must be newer than main's newest id: an ungated entry older than one a
    // viewer already saw is never announced to them. Was 2026-09-26-3 (older than main's
    // 2026-09-26-5), then 2026-09-29, which dev's #7090 entry also took; now above dev's newest.
    id: "2026-09-29-3",
    date: "2026-09-29",
    title: "Link straight to a tab on someone's profile",
    items: [
      {
        type: "improvement",
        text: "Each tab on a person's profile, like Giving or Care Notes, now has its own web address. Share or bookmark the link and it opens on that tab, and Back returns you to the tab you were on before.",
      },
    ],
  },
  {
    id: "2026-09-29",
    date: "2026-09-29",
    title: "Group leaders can answer join requests in the Member Portal",
    items: [
      {
        type: "fix",
        text: "When someone asks to join your small group, you can now approve or decline the request right on the group's page in the Member Portal. Before, the request could be made but the group leader had no way to answer it there.",
      },
      {
        type: "fix",
        text: "Group leaders are now notified when someone asks to join their group, whether the request comes from Group Finder or the Member Portal. Before, Group Finder told people the leader would be notified but never sent anything.",
      },
    ],
  },
  {
    // Own suffix: dev's own "2026-09-28" entry (from PR #7054, merged to
    // dev first) already claims the bare id for that date -- this entry
    // landed second, so it sorts after via the numeric suffix.
    id: "2026-09-28-1",
    date: "2026-09-28",
    title: "Run of Service item statuses stay accurate as you run",
    items: [
      {
        type: "fix",
        text: "During a live service, an item's status (Ready, Active, On Hold, Done, etc.) could sometimes lag behind what actually happened -- especially after editing the order mid-service, on a second device, or after recovering a session. Statuses now always reflect what's really happening.",
      },
    ],
  },
  {
    id: "2026-09-28",
    date: "2026-09-28",
    title: "More reliable service creation and Run of Service",
    requiresFlag: "run_of_service_reliability",
    items: [
      {
        type: "fix",
        text: "If a slow connection makes Forge give up waiting while you're creating, duplicating, or copying last week's service, it no longer risks creating a second copy behind the scenes — retrying now recognizes the one that already went through.",
      },
      {
        type: "fix",
        text: "Ending a live Run of Service session now completes as a single step, so a dropped connection partway through can't leave the session looking like it's still running when it actually finished.",
      },
    ],
  },
  {
    id: "2026-09-27",
    date: "2026-09-27",
    title: "Editing a live service no longer confuses where you were",
    items: [
      {
        type: "fix",
        text: "If you edited a service's order while it was live and that removed an earlier item, Run of Service could sometimes jump to the wrong \"current\" item. It now always lands on the correct one.",
      },
    ],
  },
  {
    // Own id since -4 (and everything up through it) may already be deployed.
    id: "2026-09-26-5",
    date: "2026-09-26",
    title: "Composer warns you before sending a message with missing merge fields",
    items: [
      {
        type: "fix",
        text: "If a message uses a merge field like {{first_name}} or {{phone}} and a selected recipient doesn't have that information on file, sending is now blocked and you're shown exactly who's missing what — instead of that recipient silently getting a message with a blank where their name or number should be.",
      },
      {
        type: "fix",
        text: "If sending an article by email times out, Forge now tells you the outcome is unknown and to check Message History before trying again — instead of implying it's safe to just resend, which could double-send it.",
      },
    ],
  },
  {
    id: "2026-09-26-4",
    date: "2026-09-26",
    title: "A clean start for the next person on a shared computer",
    items: [
      {
        type: "fix",
        text: "When someone signs out, or a different person signs in on the same computer, Forge now forgets everything it had loaded for the previous person, so nothing they could see carries over to the next person.",
      },
    ],
  },
  {
    id: "2026-09-26",
    date: "2026-09-26",
    title: "Volunteer names show up more often on the schedule",
    items: [
      {
        type: "fix",
        text: "In a rare case where the schedule can't load a volunteer's linked profile directly, it now still looks up and shows their real name instead of just \"Volunteer.\"",
      },
    ],
  },
  {
    id: "2026-09-25-1",
    date: "2026-09-25",
    title: "Plain-text bulk emails show your message correctly",
    items: [
      {
        type: "fix",
        text: "Sending a bulk email in plain-text mode with an apostrophe, ampersand, or angle bracket (for example \"Don't forget: rain & shine\") no longer shows recipients garbled text like &#039; or &amp; in place of the character.",
      },
    ],
  },
  {
    // Flag-gated (#6983). Shipped: on main with these two items, so it takes no more (seen is
    // keyed by id). Items added here after it shipped moved to 2026-09-29-4, the rollup's next card.
    id: "2026-09-25",
    date: "2026-09-25",
    title: "A refreshed People list",
    requiresFlag: "ui_uplift",
    requiresValue: "enabled",
    items: [
      {
        type: "improvement",
        text: "The People list keeps your place. Your view, filters, tag and sort are part of the page's web address, so a shared link opens the same list, and going Back from someone's record brings back what you had selected and where you had scrolled.",
      },
      {
        type: "improvement",
        text: "Selecting people shows a bar with Email, Add to group and more that stays on screen as you scroll, in both the table and card views. Buttons and checkboxes are larger on phones and tablets so they're easier to tap.",
      },
    ],
  },
  {
    id: "2026-09-23-2",
    date: "2026-09-23",
    title: "Finance dashboards and reports explain what they count",
    items: [
      {
        type: "fix",
        text: "Finance dashboards now use current giving records for giving trends and clearly reconcile confirmed gifts with posted General Ledger entries. Reports also say whether their numbers come from giving records, posted journal activity, or a budget combined with posted actuals, so recently imported gifts no longer look missing without an explanation.",
      },
    ],
  },
  {
    id: "2026-09-23-1",
    date: "2026-09-23",
    title: "Review imported contributions in bulk",
    items: [
      {
        type: "improvement",
        text: "Select pending staff-entered or imported contributions and mark them reviewed together. Forge excludes unsettled online payments automatically, keeps the update all-or-nothing, and includes reviewed gifts in giving totals and General Ledger posting.",
      },
    ],
  },
  {
    id: "2026-09-23",
    date: "2026-09-23",
    title: "Team roles stay within your church",
    items: [
      {
        type: "fix",
        text: "Turning Staff, Volunteer or Member on or off when you edit a person with a Forge login now updates their access. Before, saving could remove those roles instead of updating them, without any error.",
      },
      {
        type: "improvement",
        text: "Roles you give someone on your team now apply only in your church. If a person belongs to more than one church in Forge, each church manages that person's roles separately, and nothing you change affects the other church.",
      },
    ],
  },
  {
    id: "2026-09-22-2",
    date: "2026-09-22",
    title: "Finance screens load when your fiscal year hasn't been set",
    items: [
      {
        type: "fix",
        text: "The General Ledger and Journal Viewer now load for organizations that haven't set their fiscal year yet. Before, they showed \"Couldn't load this organization's fiscal calendar\" and Retry never helped. They now treat the fiscal year as starting in January and say so, with a link to Accounting Setup where you can change it.",
      },
      {
        type: "fix",
        text: "When linking a bank account or categorizing a bank transaction, the list of ledger accounts to choose from was always empty. It now shows your asset accounts.",
      },
      {
        type: "fix",
        text: "If the General Ledger or Posting History can't be loaded, Forge now tells you instead of showing an empty ledger that looks like there's nothing there.",
      },
    ],
  },
  {
    id: "2026-09-22-1",
    date: "2026-09-22",
    title: "Pay bills from Accounts Payable",
    items: [
      {
        type: "fix",
        text: "Accounts Payable has tabs again for Bills, Payments, Vendors, Inbound email and AP settings. Before, only Bills could be opened, so there was no way to pay a bill from Forge. A posted bill with a balance left now has a Pay button that opens Payments with that balance filled in, ready for you to review and post. Posting a payment run is all or nothing: if something stops it, such as a missing account setting, nothing is recorded, your amounts stay filled in, and you can fix the problem and post again. Only people whose role can pay bills see the posting controls turned on.",
      },
    ],
  },
  {
    id: "2026-09-21-6",
    date: "2026-09-21",
    title: "Online gifts finish settling",
    items: [
      {
        type: "fix",
        text: "Online gifts that Stripe successfully processed now finish settling in Forge even when Stripe uses its newer payment reference format. The original contribution keeps its gift date and now records the payment method, processing fee and net amount when it settles.",
      },
    ],
  },
  {
    id: "2026-09-21-5",
    date: "2026-09-21",
    title: "The month-end close page warns about deleted gifts still counted as income",
    items: [
      {
        type: "improvement",
        text: "If a gift was deleted but its entry is still counted as income in the General Ledger, the Month-end close page now says so, shows how much income is overstated (in the ledger's own currency), and tells you an administrator can reverse it. If an entry looks wrong, for example it is shared with another gift or points at a draft entry, the page tells you to have it reviewed first instead of running the fix. Only people who are able to run the fix see this warning, so it never shows up for someone who couldn't do anything about it. Before, nothing pointed any of this out: the only way to find one was to go through the ledger by hand.",
      },
    ],
  },
  {
    id: "2026-09-21-4",
    date: "2026-09-21",
    title: "Deleting a user keeps their history",
    items: [
      {
        type: "improvement",
        text: "When an administrator deletes a user, their sign-in and personal settings are now removed for good, along with the direct messages, saved reports and media library entries they created, and their profile details (name, email and phone) are cleared. Finance entries, calendars, emails and texts they sent, audit history and any files they uploaded are kept and stay linked to that person's account. Before, deleting someone could erase that attribution, and it could fail outright for anyone with history. If you only want to pause someone's access, use Disable instead.",
      },
    ],
  },
  {
    id: "2026-09-21-3",
    date: "2026-09-21",
    title: "Email domain setup picks up where you left off",
    items: [
      {
        type: "fix",
        text: "Opening the email domain setup again now continues with the domain your organization already has, instead of starting over and rejecting it as already added. A verified domain opens on the test-email step, and one still waiting on DNS opens on the DNS records step. If your organization has more than one sending domain, you can now switch between them on those steps, so a second domain that is still waiting on DNS can be finished. If a test email may not have gone through, retrying the same address from the same browser tab within 24 hours reuses the same request, so the provider can recognize it rather than send a second test; this does not carry over to another tab, and signing out starts fresh.",
      },
    ],
  },
  {
    id: "2026-09-21-2",
    date: "2026-09-21",
    title: "A giving statement with the wrong year",
    items: [
      {
        type: "fix",
        text: "A member's giving statement that begins on January 1 had been labeled with the previous year, so it read \"2025 Statement\" (in the portal and in the email) for a statement covering 2026. It now shows the correct year.",
      },
    ],
  },
  {
    id: "2026-09-21-1",
    date: "2026-09-21",
    title: "Reconnect QuickBooks takes you somewhere you can reconnect",
    items: [
      {
        type: "fix",
        text: "When a QuickBooks sync fails because QuickBooks was never authorized for this sync, the Reconnect QuickBooks button on Integrations now leads to a page that offers to reconnect. Before, it opened a page that could only run the failed sync again or disconnect. Organization administrators see a Reconnect button; other finance staff are told to ask an administrator. If Forge can't load the latest sync runs to check, the page says so and offers Try again alongside Reconnect, instead of hiding it.",
      },
    ],
  },
  {
    id: "2026-09-20-13",
    date: "2026-09-20",
    title: "Sync health that doesn't overclaim",
    items: [
      {
        type: "fix",
        text: "The sync health panel on Integrations no longer claims a healthy sync that has not happened. If your accounting system isn't connected, or is connected but no sync has finished yet, it says \"No successful sync yet\" instead of \"All contributions are synced\". While the status is loading or can't be loaded, it says so instead of guessing. When a sync has finished and nothing needs attention, it now reads \"No sync problems reported\". If QuickBooks has recorded a problem, such as a connection that could not be refreshed, the panel now flags it even when the last sync had succeeded, and its Retry button reruns the sync. It also checks the latest sync run itself: if the newest run failed, or started and never finished, the panel flags it instead of reporting no problems, even when an earlier sync had succeeded. If QuickBooks has to be reconnected, the panel now offers Reconnect, which opens QuickBooks settings, instead of a Retry that could not fix it.",
      },
    ],
  },
  {
    id: "2026-09-20-12",
    date: "2026-09-20",
    title: "A real test email when you set up your sending domain",
    items: [
      {
        type: "fix",
        text: "The test step in the email setup wizard now sends a real test email from your domain, and only says it was sent once the email service has accepted it. If it can't send, for example because the domain isn't verified yet, it tells you why instead of reporting success. The preview shows exactly what will be sent.",
      },
      {
        type: "fix",
        text: "Adding a sending domain, checking whether it is verified, removing it and choosing a default now reach the email service correctly from the setup wizard.",
      },
    ],
  },
  {
    id: "2026-09-20-11",
    date: "2026-09-20",
    title: "A person's Requests card shows their real requests",
    items: [
      {
        type: "fix",
        text: "On a person's profile, the Requests card now lists the requests they have raised, with their status and date, instead of always saying \"No linked requests.\" It lists only the requests you are allowed to open, so private and restricted ones stay private. If the requests can't be loaded it says so and offers Try again.",
      },
      {
        type: "fix",
        text: "Household cards no longer show \"Total giving $0\". That figure was never calculated, so the card leaves it out until it can show a real one.",
      },
    ],
  },
  {
    id: "2026-09-20-10",
    date: "2026-09-20",
    title: "Kids Check-in says when it couldn't check guardians",
    items: [
      {
        type: "fix",
        text: "On Kids Check-in, if the check for children without an authorized parent or guardian can't be completed, admins now see a notice with a Try again button. Before, the reminder simply disappeared, which looked the same as every child having a guardian on record.",
      },
      {
        type: "fix",
        text: "On a child's profile, if their parents and guardians can't be loaded, the card now says so and offers Try again, instead of saying \"No parents or guardians on file.\"",
      },
    ],
  },
  {
    id: "2026-09-20-9",
    date: "2026-09-20",
    title: "Evening online gifts get the right date",
    items: [
      {
        type: "fix",
        text: "An online gift given in the evening is now dated on the day it was given where your church is, not the next day. This matters most on New Year's Eve, when a late gift could otherwise have been filed under the next year's giving statement and tax total. Your church's time zone comes from Settings; until one is set, Eastern Time is used.",
      },
    ],
  },
  {
    id: "2026-09-20-5",
    date: "2026-09-20",
    title: "Message health that tells the truth",
    items: [
      {
        type: "fix",
        text: "The Message Health Center no longer says your email domain is verified when it isn't. SPF and DKIM now come from your sending domain's actual DNS checks, and read \"Not set up\" until you add a domain, with a link to set one up.",
      },
      {
        type: "fix",
        text: "A church that hasn't sent any messages yet now sees \"No messages yet\" instead of \"100% delivered\" and \"Healthy\".",
      },
      {
        type: "fix",
        text: "If delivery numbers can't be loaded, the page says so and lets you retry, instead of showing a perfect score.",
      },
    ],
  },
  {
    id: "2026-09-20-4",
    date: "2026-09-20",
    title: "HR, people and report dates that match your records",
    items: [
      {
        type: "fix",
        text: "Dates no longer show a day early on the HR pages: staff start dates, time off, offboarding last days, review cycles and due dates, and compensation history. A leave from December 24 to 26 now reads December 24 to 26.",
      },
      {
        type: "fix",
        text: "Life events show and confirm the right date, and an event on January 1 now counts in that year's totals instead of the year before.",
      },
      {
        type: "fix",
        text: "Education dates that start on the first of a month, such as September 2019, no longer read as the month before, and a registration request's date of birth shows the recorded day.",
      },
      {
        type: "fix",
        text: "The SMS usage table and the Monthly Board Summary header now name the right month. They were reading one month early.",
      },
      {
        type: "fix",
        text: "The Volunteer Schedule and Bank Reconciliation reports show the recorded date for each row instead of the day before.",
      },
    ],
  },
  {
    id: "2026-09-20-3",
    date: "2026-09-20",
    title: "Member and task dates that match your records",
    items: [
      {
        type: "fix",
        text: "Dates no longer show a day early in the member portal and bulletins: the last gift date, sermon dates, and each bulletin's \"Week of\" date, including on the public bulletin page.",
      },
      {
        type: "fix",
        text: "The attendance history's date filter now includes services on the start and end days you pick, and no longer includes one from the day after.",
      },
      {
        type: "fix",
        text: "The bulletin builder no longer pulls the previous week's services for a bulletin dated on a Sunday.",
      },
      {
        type: "fix",
        text: "A task due today is no longer counted as overdue on the Home dashboard, and one due tomorrow is no longer listed under Today. \"Days past due\" now counts whole days, so a task due today doesn't show as one day past due.",
      },
      {
        type: "fix",
        text: "\"Last seen\", \"Last attended\" and \"Last gift\" dates in people and engagement views now match the recorded day.",
      },
    ],
  },
  {
    id: "2026-09-20-2",
    date: "2026-09-20",
    title: "Dates that match your records",
    items: [
      {
        type: "fix",
        text: "Dates on the General Ledger, Restricted Releases, Allocation Rules, Conditional Contributions and Pledge Reminders pages no longer show a day early. A January 1 entry now reads January 1, not December 31. The automatic name for a new bank deposit (\"Deposit – Sep 19, 2026\") uses the date you picked, not the day before.",
      },
      {
        type: "fix",
        text: "A giving statement that starts on January 1 now files under the right year in the member portal, instead of the year before.",
      },
      {
        type: "fix",
        text: "Late in the evening, dates that default to today no longer jump to tomorrow. This covers pastoral notes and school autopay (whose start dates and invoice cutoffs now use your organization's calendar day, wherever you're signed in from), and the recurring-giving \"Past due\" flag now follows your organization's calendar day, so tonight's charge is no longer marked overdue.",
      },
    ],
  },
  {
    id: "2026-09-20-1",
    date: "2026-09-20",
    title: "Pledge numbers members can trust",
    items: [
      {
        type: "fix",
        text: "The suggested installment on a member's pledge card now works out what it takes to finish the pledge from today: the balance still owed, spread over the payments still ahead, at the pledge's own weekly, monthly, quarterly or yearly pace. It says which pace and by when. Before, it split the full target into one payment too many and ignored what had already been given.",
      },
      {
        type: "fix",
        text: "The pledges list on the Giving page now reads the same progress numbers as the pledge card, instead of showing $0.00 and a broken percentage.",
      },
      {
        type: "fix",
        text: "A pledge for calendar year 2026 is no longer labeled 2025–2026 on the member's pledge card, and the campaign dates shown when making a pledge no longer land a day early.",
      },
      {
        type: "fix",
        text: "\"Received to date\" on a member's pledge card now matches what staff see for that pledge, counting gifts matched to it as well as gifts given straight toward it, instead of showing $0.",
      },
    ],
  },
  {
    id: "2026-09-20",
    date: "2026-09-20",
    title: "Void journal entries you no longer need",
    items: [
      {
        type: "feature",
        text: "Finance staff can now void a posted journal entry that has no source record, one at a time or several at once, from the Journal Viewer. Entries created by gifts, bills, invoices, transfers and other workflows are still voided from their own screens. Before you confirm, you see which entries will be voided and which will be skipped, and why. Voiding keeps the original and adds an offsetting entry on the same date, so your history stays complete. You'll be asked for a reason.",
      },
      {
        type: "improvement",
        text: "A voided entry and the entry that offsets it are now labeled and linked to each other, and a new \"Hide voided pairs\" switch tidies the list once you've cleaned up.",
      },
      {
        type: "improvement",
        text: "Entries that can't be voided from the Journal Viewer now say why and link to the screen that created them, such as Contributions or Accounts Payable, and say so when that screen can't void them yet. Entries dated in a closed month say that the month has to be reopened first.",
      },
      {
        type: "fix",
        text: "Entries with no source record no longer show a link that went nowhere.",
      },
    ],
  },
  {
    id: "2026-09-18",
    date: "2026-09-18",
    title: "Smoother month-end close and giving review",
    items: [
      {
        type: "improvement",
        text: "Month-end close now walks you through its checklist in order. Each step unlocks after the one before it, and the hard close button stays off until every step is done and every gift has been reviewed.",
      },
      {
        type: "improvement",
        text: "The month-end close page now also shows gifts that are ready to post to the ledger but haven't been posted yet, with a button to the posting queue. You can still close with a written reason if some can't be posted.",
      },
      {
        type: "improvement",
        text: "The month-end close page now lists any gifts still waiting for review, with a button that takes you straight to them.",
      },
      {
        type: "fix",
        text: "When you save a batch of gifts with \"post to the ledger\" turned on, that choice is now saved together with the batch, so it is no longer lost if something goes wrong right after saving.",
      },
      {
        type: "improvement",
        text: "Importing giving from a spreadsheet now handles older records: donor names, guest givers, funds, batches, pledges, and gifts split across several funds. Pledge choices show only the giver's own pledges.",
      },
      {
        type: "fix",
        text: "Opening tasks from a project now shows that project's tasks, and a task you add from there stays attached to the project.",
      },
      {
        type: "improvement",
        text: "Gifts you enter by hand no longer email the donor a receipt automatically. Open the gift and choose Send Receipt when you want them to have one.",
      },
      {
        type: "improvement",
        text: "If a gift is posted for a month that has already been closed, it is now booked in the current month instead of slipping into the closed one, and the original date is kept in the record. If the current month is closed too, the gift stays in the posting queue with a clear message.",
      },
      {
        type: "improvement",
        text: "\"Open posting queue\" on the month-end close page now shows only the gifts for the period you are closing, and lets you page through more than 200.",
      },
      {
        type: "fix",
        text: "If your role can see that gifts are waiting to be posted but can't open them, the posting queue now says so and tells you what to do next (ask a bookkeeper or finance admin to post them, or close with a written reason), instead of showing an error that goes nowhere.",
      },
      {
        type: "fix",
        text: "The contribution import now tells you when it can't load your contributors, funds or pledges and lets you retry, instead of reporting them as missing.",
      },
      {
        type: "fix",
        text: "If marking a gift as reviewed doesn't work, for example because someone already reviewed it, you now see why instead of nothing happening.",
      },
      {
        type: "improvement",
        text: "Clearer wording for failed syncs and for what a soft close does. It only marks the period; people can still post.",
      },
    ],
  },
  {
    id: "2026-09-13",
    date: "2026-09-13",
    title: "See what's new, right in Forge",
    items: [
      {
        type: "feature",
        text: "A \"What's new\" panel now shows recent improvements after each release — open it anytime from the top bar.",
      },
    ],
  },
];
